<template>
  <div
    class="preparation-board"
    :style="themeStyle"
  >
    <div class="prep-settings">
      <b-button
        variant="flat-primary"
        class="prep-settings-btn"
        @click="toggleSettings"
      >
        <feather-icon
          icon="SettingsIcon"
          size="18"
        />
      </b-button>
      <transition name="prep-settings-pop">
        <div
          v-if="settingsOpen"
          class="prep-settings-panel"
        >
          <div class="prep-settings-header">
            <div class="prep-settings-title">
              Beállítások
            </div>
            <b-button
              variant="flat-dark"
              class="prep-settings-close"
              @click="closeSettings"
            >
              ×
            </b-button>
          </div>

          <div class="prep-settings-menu">
            <button
              class="prep-settings-menu-item"
              @click="toggleSection('background')"
            >
              Háttér
              <feather-icon
                :icon="openSections.background ? 'ChevronUpIcon' : 'ChevronDownIcon'"
                size="16"
              />
            </button>
            <div
              v-show="openSections.background"
              class="prep-settings-submenu"
            >
              <button
                class="prep-settings-subitem"
                @click="openColorPicker('backgroundColor')"
              >
                Szín
              </button>
              <button
                class="prep-settings-subitem"
                @click="openImagePicker"
              >
                Kép
              </button>
            </div>

            <button
              class="prep-settings-menu-item"
              @click="toggleSection('text')"
            >
              Felirat
              <feather-icon
                :icon="openSections.text ? 'ChevronUpIcon' : 'ChevronDownIcon'"
                size="16"
              />
            </button>
            <div
              v-show="openSections.text"
              class="prep-settings-submenu"
            >
              <button
                class="prep-settings-subitem"
                @click="openFontPicker"
              >
                Stílus
              </button>
              <button
                class="prep-settings-subitem"
                @click="openColorPicker('fontColor')"
              >
                Szín
              </button>
            </div>

            <button
              class="prep-settings-menu-item"
              @click="toggleSection('card')"
            >
              Kártya
              <feather-icon
                :icon="openSections.card ? 'ChevronUpIcon' : 'ChevronDownIcon'"
                size="16"
              />
            </button>
            <div
              v-show="openSections.card"
              class="prep-settings-submenu"
            >
              <button
                class="prep-settings-subitem"
                @click="openColorPicker('cardBackground')"
              >
                Háttér
              </button>
              <button
                class="prep-settings-subitem"
                @click="openColorPicker('cardBorder')"
              >
                Keret
              </button>
              <button
                class="prep-settings-subitem"
                @click="openShadowPicker"
              >
                Árnyék
              </button>
            </div>

            <button
              class="prep-settings-menu-item"
              @click="toggleSection('style')"
            >
              Sílus
              <feather-icon
                :icon="openSections.style ? 'ChevronUpIcon' : 'ChevronDownIcon'"
                size="16"
              />
            </button>
            <div
              v-show="openSections.style"
              class="prep-settings-submenu prep-style-submenu"
            >
              <button
                v-for="preset in stylePresets"
                :key="preset.key"
                class="prep-settings-subitem prep-style-item"
                :class="{ active: settingsDraft.stylePreset === preset.key }"
                @click="applyStylePreset(preset.key)"
              >
                <span class="prep-style-item-label">
                  <span
                    class="prep-style-dot"
                    :style="{ backgroundColor: preset.accentColor }"
                  />
                  {{ preset.label }}
                </span>
                <span class="prep-style-swatches">
                  <span
                    v-for="(swatch, index) in preset.swatches"
                    :key="`${preset.key}-swatch-${index}`"
                    class="prep-style-swatch"
                    :style="{ backgroundColor: swatch }"
                  />
                </span>
              </button>
            </div>
          </div>

          <div class="prep-settings-actions">
            <b-button
              variant="outline-danger"
              @click="resetSettings"
            >
              Alapértelmezett
            </b-button>
            <b-button
              variant="outline-secondary"
              @click="closeSettings"
            >
              Mégse
            </b-button>
            <b-button
              variant="primary"
              @click="saveSettings"
            >
              Mentés
            </b-button>
          </div>
        </div>
      </transition>
    </div>
    <div
      v-if="canManageAssignments && !detailOffer"
      class="prep-card-search"
    >
      <b-form-input
        v-model.trim="leaderCardSearch"
        placeholder="Keresés kártyák között..."
      />
    </div>

    <b-modal
      v-model="colorModalOpen"
      title="Szín választó"
      hide-footer
      centered
      size="sm"
      body-class="prep-color-modal"
    >
      <sketch-picker
        :value="colorPickerValue"
        :preset-colors="colorSwatches"
        @input="onColorInput"
      />
      <div class="prep-settings-actions mt-50">
        <b-button
          variant="outline-secondary"
          @click="colorModalOpen = false"
        >
          Mégse
        </b-button>
        <b-button
          variant="primary"
          @click="applyColorSelection"
        >
          Ok
        </b-button>
      </div>
    </b-modal>

    <b-modal
      v-model="fontModalOpen"
      title="Betűstílus"
      hide-footer
      centered
      size="sm"
      body-class="prep-color-modal"
    >
      <b-form-select
        v-model="settingsDraft.fontFamily"
        :options="fontOptions"
      />
      <div class="prep-settings-actions mt-50">
        <b-button
          variant="outline-secondary"
          @click="fontModalOpen = false"
        >
          Bezár
        </b-button>
      </div>
    </b-modal>

    <b-modal
      v-model="shadowModalOpen"
      title="Kártya árnyék"
      hide-footer
      centered
      size="sm"
      body-class="prep-color-modal"
    >
      <b-form-select
        v-model="settingsDraft.cardShadow"
        :options="shadowOptions"
      />
      <div class="prep-settings-actions mt-50">
        <b-button
          variant="outline-secondary"
          @click="shadowModalOpen = false"
        >
          Bezár
        </b-button>
      </div>
    </b-modal>

    <b-modal
      v-model="imageModalOpen"
      title="Háttérkép"
      hide-footer
      centered
      size="sm"
      body-class="prep-color-modal"
    >
      <b-form-file
        v-model="backgroundImageFile"
        accept=".jpg,.jpeg,.png,.webp"
      />
      <small class="text-muted d-block mt-25">Kép kiválasztása (kereső)</small>
      <b-button
        v-if="settingsDraft.backgroundImage"
        variant="outline-danger"
        size="sm"
        class="mt-50"
        @click="removeBackgroundImage"
      >
        Háttérkép törlése
      </b-button>
      <div class="prep-settings-actions mt-50">
        <b-button
          variant="outline-secondary"
          @click="imageModalOpen = false"
        >
          Bezár
        </b-button>
      </div>
    </b-modal>

    <div
      v-if="correctionModalOpen"
      class="prep-correction-console"
      :style="correctionConsoleStyle"
    >
      <div
        class="prep-correction-console-header"
        @mousedown.prevent="startCorrectionDrag"
      >
        <div class="prep-correction-console-title">
          Korrekciós konzol
        </div>
        <button
          type="button"
          class="prep-correction-console-close"
          @click="cancelCorrectionChanges"
        >
          ×
        </button>
      </div>
      <div class="prep-correction-console-body">
        <div class="prep-correction-row">
          <label class="prep-correction-label">
            Globális módosítás
          </label>
          <div class="prep-correction-control">
            <span class="prep-correction-value">
              {{ correctionDraftGlobalPercent > 0 ? '+' : '' }}{{ correctionDraftGlobalPercent }}%
            </span>
            <div class="prep-correction-arrows">
              <button
                type="button"
                class="prep-correction-arrow"
                @click="adjustCorrectionPercent('global', 1)"
              >
                <feather-icon
                  icon="ChevronUpIcon"
                  size="12"
                />
              </button>
              <button
                type="button"
                class="prep-correction-arrow"
                @click="adjustCorrectionPercent('global', -1)"
              >
                <feather-icon
                  icon="ChevronDownIcon"
                  size="12"
                />
              </button>
            </div>
          </div>
        </div>
        <div class="prep-correction-row">
          <label class="prep-correction-label">
            Anyag ár módosítás
          </label>
          <div class="prep-correction-control">
            <span class="prep-correction-value">
              {{ correctionDraftMaterialPercent > 0 ? '+' : '' }}{{ correctionDraftMaterialPercent }}%
            </span>
            <div class="prep-correction-arrows">
              <button
                type="button"
                class="prep-correction-arrow"
                @click="adjustCorrectionPercent('material', 1)"
              >
                <feather-icon
                  icon="ChevronUpIcon"
                  size="12"
                />
              </button>
              <button
                type="button"
                class="prep-correction-arrow"
                @click="adjustCorrectionPercent('material', -1)"
              >
                <feather-icon
                  icon="ChevronDownIcon"
                  size="12"
                />
              </button>
            </div>
          </div>
        </div>
        <div class="prep-correction-row">
          <label class="prep-correction-label">
            Díj ár módosítás
          </label>
          <div class="prep-correction-control">
            <span class="prep-correction-value">
              {{ correctionDraftFeePercent > 0 ? '+' : '' }}{{ correctionDraftFeePercent }}%
            </span>
            <div class="prep-correction-arrows">
              <button
                type="button"
                class="prep-correction-arrow"
                @click="adjustCorrectionPercent('fee', 1)"
              >
                <feather-icon
                  icon="ChevronUpIcon"
                  size="12"
                />
              </button>
              <button
                type="button"
                class="prep-correction-arrow"
                @click="adjustCorrectionPercent('fee', -1)"
              >
                <feather-icon
                  icon="ChevronDownIcon"
                  size="12"
                />
              </button>
            </div>
          </div>
        </div>
        <div class="prep-correction-row prep-correction-row-negotiated">
          <label class="prep-correction-label">
            Kialkudott ár
          </label>
          <div class="prep-correction-negotiated-wrap">
            <div class="prep-correction-negotiated-input-wrap">
              <b-form-input
                :value="formatNegotiatedNetInput(correctionDraftNegotiatedNet)"
                type="text"
                class="prep-correction-negotiated-input"
                @input="onNegotiatedNetInput"
                @blur="normalizeNegotiatedNet"
              />
              <span class="prep-correction-negotiated-currency">Ft</span>
            </div>
            <div class="prep-correction-negotiated-options">
              <b-form-checkbox
                v-model="correctionNegotiatedAffectsMaterial"
                @change="applyCorrectionPreviewChanges"
              >
                Anyag
              </b-form-checkbox>
              <b-form-checkbox
                v-model="correctionNegotiatedAffectsFee"
                @change="applyCorrectionPreviewChanges"
              >
                Díj
              </b-form-checkbox>
            </div>
          </div>
        </div>
        <div class="prep-settings-actions mt-50">
          <b-button
            variant="primary"
            :disabled="correctionSaving"
            @click="saveCorrectionChanges"
          >
            {{ correctionSaving ? 'Mentés...' : 'Mentés' }}
          </b-button>
        </div>
      </div>
    </div>

    <b-modal
      v-model="addBudgetModalOpen"
      :title="addBudgetModalMode === 'sub' ? 'Almenü hozzáadása' : 'Elem hozzáadása'"
      centered
      size="sm"
      @ok.prevent="confirmAddBudgetMenu"
      @hidden="resetAddBudgetMenuDraft"
    >
      <b-form-select
        v-model="addBudgetMenuDraft"
        :options="activeAddBudgetMenuOptions"
      />
      <b-form-input
        v-if="addBudgetCreateMode"
        v-model="addBudgetNewMenuTitle"
        class="mt-50"
        :placeholder="addBudgetModalMode === 'sub' ? 'Új almenü neve' : 'Új főmenü neve'"
      />
      <template #modal-footer="{ ok, cancel }">
        <div class="w-100 d-flex align-items-center justify-content-between">
          <b-button
            variant="outline-primary"
            class="prep-add-plus-btn"
            @click="toggleCreateMainMenuMode"
          >
            +
          </b-button>
          <b-button
            variant="outline-danger"
            class="prep-add-delete-btn ml-50"
            :disabled="!canDeleteAddBudgetSelection"
            @click="deleteSelectedAddBudgetOption"
          >
            <feather-icon
              icon="Trash2Icon"
              size="14"
            />
          </b-button>
          <div class="d-flex align-items-center ml-auto">
            <b-button
              variant="secondary"
              class="mr-50"
              @click="cancel()"
            >
              Cancel
            </b-button>
            <b-button
              variant="primary"
              @click="ok()"
            >
              OK
            </b-button>
          </div>
        </div>
      </template>
    </b-modal>

    <div
      v-if="showAssigneeBox && !detailOffer"
      class="prep-assignee-strip"
    >
      <div class="prep-canvas-assignee-box">
        <button
          v-for="user in users"
          :key="user.id"
          type="button"
          :class="[
            'prep-assignee-pill',
            user.readOnlyBucket ? 'prep-assignee-pill-readonly' : 'prep-user-drop',
            selectedAssigneeId === user.id ? 'active' : '',
          ]"
          :data-user-id="user.id"
          :data-leader="isGroupLeader && String(user.id) === String(currentUserId) ? '1' : '0'"
          @click="onUserCardClick(user)"
        >
          <div class="prep-assignee-pill-name">
            {{ user.name }}
          </div>
          <div class="prep-assignee-pill-role">
            {{ user.title }}
          </div>
        </button>
      </div>
    </div>

    <div
      v-if="!detailOffer"
      ref="canvas"
      class="prep-canvas"
      :style="canvasStyle"
    >
      <b-card
        v-for="offer in filteredBudgetCards"
        :key="offer.key"
        class="prep-tile budget prep-draggable prep-budget-card"
        :data-id="offer.key"
        :data-offer-id="offer.offerId"
        :data-offer-type="offer.offerType"
        :style="cardStyle(offer.key)"
        :data-x="getPos(offer.key).x"
        :data-y="getPos(offer.key).y"
        @dblclick.stop="openBudgetDetail(offer)"
      >
        <div class="prep-handle">
          ⋮⋮
        </div>
        <div class="prep-budget">
          <div class="prep-budget-title">
            {{ getOfferTitle(offer) }}
          </div>
          <div class="prep-budget-customer">
            {{ getOfferCustomer(offer) }}
          </div>
        </div>
      </b-card>

      <b-card
        v-for="offer in visibleAssignedOffers"
        :key="offer.key"
        class="prep-tile assigned prep-draggable prep-budget-card"
        :data-id="offer.key"
        :data-offer-id="offer.offerId"
        :data-offer-type="offer.offerType"
        :style="cardStyle(offer.key)"
        :data-x="getPos(offer.key).x"
        :data-y="getPos(offer.key).y"
        @dblclick.stop="openBudgetDetail(offer)"
      >
        <div class="prep-handle">
          ⋮⋮
        </div>
        <div class="prep-budget">
          <div class="prep-budget-title">
            {{ getOfferTitle(offer) }}
          </div>
          <div class="prep-budget-customer">
            {{ getOfferCustomer(offer) }}
          </div>
        </div>
      </b-card>
    </div>

    <div
      v-else
      class="prep-detail"
      :style="canvasStyle"
    >
      <div class="prep-detail-body">
        <budget-deep-foundation-table
          v-if="contentBudgetMenu && contentBudgetMenu.endpoint"
          ref="importTarget"
          :endpoint="contentBudgetMenu.endpoint"
          :category-key="contentBudgetMenu.categoryKey"
          :subgroup-key="contentBudgetMenu.subgroupKey"
          :subgroup-options="activeSubgroupOptions"
          :edit-mode="selectedCustomEditMode"
          :show-positive-qty-only="selectedCustomShowPositiveQtyOnly"
          :offer-type="activeBudgetOfferType"
          :offer-id="activeBudgetOfferId"
        />
        <div
          v-else-if="selectedCustomBudgetMenu && selectedCustomBudgetMenu.type === 'uj'"
          class="prep-detail-placeholder"
        >
          Új költségvetés elem létrehozása: fejlesztés alatt.
        </div>
        <budget-deep-foundation-table
          v-else-if="isFormworkSelected"
          ref="importTarget"
          endpoint="budget/items/zsaluzas-allvanyozas"
          category-key="zsaluzas-allvanyozas"
          :offer-type="activeBudgetOfferType"
          :offer-id="activeBudgetOfferId"
        />
        <budget-deep-foundation-table
          v-else-if="isStripFoundationSelected"
          ref="importTarget"
          endpoint="budget/items/sikalapozas"
          category-key="sikalapozas"
          :offer-type="activeBudgetOfferType"
          :offer-id="activeBudgetOfferId"
        />
        <budget-deep-foundation-table
          v-else-if="isAlapozasSelected"
          ref="importTarget"
          endpoint="budget/items/alapozas"
          category-key="alapozas"
          :offer-type="activeBudgetOfferType"
          :offer-id="activeBudgetOfferId"
        />
        <budget-deep-foundation-table
          v-else-if="isWaterproofingSelected"
          ref="importTarget"
          endpoint="budget/items/viztelenites"
          category-key="viztelenites"
          :offer-type="activeBudgetOfferType"
          :offer-id="activeBudgetOfferId"
        />
        <budget-deep-foundation-table
          v-else-if="isEarthworkSelected"
          ref="importTarget"
          endpoint="budget/items/foldmunka"
          category-key="foldmunka"
          :offer-type="activeBudgetOfferType"
          :offer-id="activeBudgetOfferId"
        />
        <budget-deep-foundation-table
          v-else-if="isEarthwork2Selected"
          ref="importTarget"
          endpoint="budget/items/foldmunka-2"
          category-key="foldmunka-2"
          :offer-type="activeBudgetOfferType"
          :offer-id="activeBudgetOfferId"
        />
        <budget-main-summary
          v-else-if="isMainSummarySelected"
          :offer-type="activeBudgetOfferType"
          :offer-id="activeBudgetOfferId"
          :offer-name="(detailOffer && detailOffer.projectName) || ''"
        />
        <budget-deep-foundation-table
          v-else-if="isOsszesitoSelected"
          ref="importTarget"
          endpoint="budget/items/osszesito"
          category-key="osszesito"
          :offer-type="activeBudgetOfferType"
          :offer-id="activeBudgetOfferId"
        />
        <budget-deep-foundation-table
          v-else-if="isDeepFoundationSelected"
          ref="importTarget"
          endpoint="budget/items/mely-alapozas"
          category-key="melyalapozas"
          :offer-type="activeBudgetOfferType"
          :offer-id="activeBudgetOfferId"
        />
        <budget-deep-foundation-table
          v-else-if="isConcreteSelected"
          ref="importTarget"
          endpoint="budget/items/helyszini-beton-vasbeton"
          category-key="helyszini-beton-vasbeton"
          :offer-type="activeBudgetOfferType"
          :offer-id="activeBudgetOfferId"
        />
        <budget-deep-foundation-table
          v-else-if="isOtherSelected"
          ref="importTarget"
          endpoint="budget/items/egyeb"
          category-key="egyeb"
          :offer-type="activeBudgetOfferType"
          :offer-id="activeBudgetOfferId"
        />
        <budget-deep-foundation-table
          v-else-if="isOptionalWorksSelected"
          ref="importTarget"
          endpoint="budget/items/opcios-munkak"
          category-key="opcios-munkak"
          :offer-type="activeBudgetOfferType"
          :offer-id="activeBudgetOfferId"
        />
        <budget-deep-foundation-table
          v-else-if="isJarulekosSelected"
          ref="importTarget"
          endpoint="budget/items/jarulekos-koltsegek"
          category-key="jarulekos-koltsegek"
          :offer-type="activeBudgetOfferType"
          :offer-id="activeBudgetOfferId"
        />
        <div
          v-else
          class="prep-detail-placeholder"
        >
          Válassz almenüt a bal oldali menüből
        </div>
      </div>
    </div>

  </div>
</template>

<script>
import {
  BCard,
  BButton,
  BModal,
  BFormSelect,
  BFormFile,
  BFormInput,
  BFormCheckbox,
} from 'bootstrap-vue'
import { Sketch } from 'vue-color'
import interact from 'interactjs'
import axios from 'axios'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import BudgetDeepFoundationTable from './BudgetDeepFoundationList.vue'
import BudgetMainSummary from './BudgetMainSummary.vue'

export default {
  components: {
    BCard,
    BButton,
    BModal,
    BFormSelect,
    BFormFile,
    BFormInput,
    BFormCheckbox,
    'sketch-picker': Sketch,
    BudgetDeepFoundationTable,
    BudgetMainSummary,
  },
  data() {
    return {
      users: [],
      positions: {},
      currentUserId: null,
      dragFrame: null,
      dragPending: null,
      lastDropActionAt: 0,
      settingsOpen: false,
      backgroundImageFile: null,
      uiSettings: {
        backgroundType: 'color',
        backgroundColor: '#0f172a',
        backgroundImage: '',
        stylePreset: '',
        fontFamily: '"Inter", "Segoe UI", sans-serif',
        fontColor: '#e5e7eb',
        cardBackground: '#1c233a',
        cardTextColor: '#e5e7eb',
        cardBorder: 'rgba(148, 163, 184, 0.18)',
        cardRadius: 14,
        cardShadow: 'medium',
        accentColor: '#60a5fa',
      },
      settingsDraft: {},
      previewBackgroundUrl: '',
      colorModalOpen: false,
      fontModalOpen: false,
      shadowModalOpen: false,
      imageModalOpen: false,
      correctionModalOpen: false,
      correctionSaving: false,
      correctionConsoleX: 650,
      correctionConsoleY: 380,
      correctionDragging: false,
      correctionDragOffsetX: 0,
      correctionDragOffsetY: 0,
      addBudgetMenuDraft: 'zsaluzas-allvanyozas',
      addBudgetNewMenuTitle: '',
      addBudgetCreateMode: false,
      correctionSettings: {
        globalPercent: 0,
        materialPercent: 0,
        feePercent: 0,
      },
      correctionDraftGlobalPercent: 0,
      correctionDraftMaterialPercent: 0,
      correctionDraftFeePercent: 0,
      correctionDraftNegotiatedNet: 0,
      correctionNegotiatedBaseNet: 0,
      correctionSummaryBaseMaterial: 0,
      correctionSummaryBaseFee: 0,
      correctionNegotiatedAffectsMaterial: true,
      correctionNegotiatedAffectsFee: true,
      addBudgetMenuOptionsSub: [
        { value: 'savalap', text: 'Sávalap' },
        { value: 'pontalap', text: 'pont alap' },
        { value: 'talpgerenda', text: 'talpgerenda' },
        { value: 'alaplemez', text: 'Alaplemez' },
        { value: 'pince-szint', text: 'pince szint' },
        { value: 'foldszint', text: 'földszint' },
        { value: 'emelet-plusz', text: '+ emelet' },
        { value: 'zaro-szint', text: 'záró szint' },
        { value: 'medence', text: 'Medence' },
        { value: 'alatamasztas', text: 'Alátámasztás' },
        { value: 'seged-allvany', text: 'segéd állvány' },
        { value: 'seged-anyagok', text: 'Segéd anyagok' },
      ],
      colorPickerTarget: 'backgroundColor',
      colorPickerValue: '#0f172a',
      syncingFixedSikalapozasMenu: false,
      colorSwatches: [
        '#000000', '#1f2937', '#334155', '#64748b', '#94a3b8', '#e2e8f0', '#ffffff',
        '#fbbf24', '#f59e0b', '#f97316', '#facc15', '#fde047',
        '#22c55e', '#16a34a', '#10b981',
        '#0ea5e9', '#3b82f6', '#6366f1',
        '#a855f7', '#ec4899', '#ef4444',
      ],
      colorPalette: [
        { value: '#0f172a', text: 'Éjkék' },
        { value: '#111827', text: 'Grafit' },
        { value: '#1c233a', text: 'Sötétkék' },
        { value: '#1f2937', text: 'Szén' },
        { value: '#334155', text: 'Palakék' },
        { value: '#0ea5e9', text: 'Égkék' },
        { value: '#60a5fa', text: 'Kék' },
        { value: '#22c55e', text: 'Zöld' },
        { value: '#f59e0b', text: 'Narancs' },
        { value: '#ef4444', text: 'Piros' },
        { value: '#e5e7eb', text: 'Világos' },
        { value: '#ffffff', text: 'Fehér' },
      ],
      stylePresets: [
        {
          key: 'aurora-neon',
          label: 'Aurora Neon',
          accentColor: '#8b5cf6',
          backgroundColor: '#0b0f2a',
          cardBackground: '#17163b',
          cardBorder: '#7c3aed66',
          fontColor: '#ece9ff',
          warningColor: '#f59e0b',
          dangerColor: '#fb7185',
          rowAccents: ['#f472b6', '#f59e0b', '#22d3ee', '#8b5cf6', '#f43f5e', '#34d399'],
          swatches: ['#0b0f2a', '#17163b', '#8b5cf6', '#f472b6', '#22d3ee'],
        },
        {
          key: 'sunset-dark',
          label: 'Sunset Darkwave',
          accentColor: '#f97316',
          backgroundColor: '#131124',
          cardBackground: '#1e1a33',
          cardBorder: '#fb718566',
          fontColor: '#fff3f0',
          warningColor: '#facc15',
          dangerColor: '#fb7185',
          rowAccents: ['#fb7185', '#f97316', '#facc15', '#a78bfa', '#38bdf8', '#34d399'],
          swatches: ['#131124', '#1e1a33', '#fb7185', '#f97316', '#facc15'],
        },
        {
          key: 'frosted-pop',
          label: 'Frosted Pop',
          accentColor: '#0ea5e9',
          backgroundColor: '#eef4ff',
          cardBackground: '#ffffff',
          cardBorder: '#bfdbfe',
          fontColor: '#1f2937',
          warningColor: '#f59e0b',
          dangerColor: '#ef4444',
          rowAccents: ['#fb7185', '#f59e0b', '#22c55e', '#38bdf8', '#8b5cf6', '#6366f1'],
          swatches: ['#eef4ff', '#ffffff', '#38bdf8', '#a78bfa', '#f59e0b'],
        },
        {
          key: 'candy-panels',
          label: 'Candy Panels',
          accentColor: '#ec4899',
          backgroundColor: '#141735',
          cardBackground: '#1e2247',
          cardBorder: '#22d3ee66',
          fontColor: '#f8f9ff',
          warningColor: '#fbbf24',
          dangerColor: '#f43f5e',
          rowAccents: ['#ec4899', '#f59e0b', '#22c55e', '#3b82f6', '#d946ef', '#f97316'],
          swatches: ['#141735', '#1e2247', '#ec4899', '#22d3ee', '#fbbf24'],
        },
      ],
      fontOptions: [
        { value: '"Inter", "Segoe UI", sans-serif', text: 'Inter / Segoe' },
        { value: '"Poppins", "Segoe UI", sans-serif', text: 'Poppins' },
        { value: '"Nunito", "Segoe UI", sans-serif', text: 'Nunito' },
        { value: '"Montserrat", "Segoe UI", sans-serif', text: 'Montserrat' },
      ],
      shadowOptions: [
        { value: 'light', text: 'Finom' },
        { value: 'medium', text: 'Közepes' },
        { value: 'strong', text: 'Erős' },
      ],
      openSections: {
        background: true,
        text: true,
        card: true,
        style: true,
      },
      budgetCards: [],
      isGroupLeader: false,
      canManageAssignments: false,
      canReturnToLeader: false,
      notProjectOffers: [],
      completedProjectOffers: [],
      assignedOffersByUser: {},
      assigneeLookupByName: {},
      selectedAssigneeId: null,
      detailOffer: null,
      leaderAutoRefreshTimer: null,
      leaderCardSearch: '',
      prepLogo: require('@/assets/images/logo/logo.svg'),
      menuConfigLoadedKey: '',
      menuConfigPersistTimer: null,
      suppressMenuConfigPersist: false,
    }
  },
  computed: {
    showAssigneeBox() {
      return this.canManageAssignments || this.canReturnToLeader
    },
    activeSettings() {
      return this.settingsOpen ? this.settingsDraft : this.uiSettings
    },
    themeStyle() {
      const settings = this.activeSettings
      const selectedPreset = this.stylePresets.find(item => item.key === settings.stylePreset)
      const presetSwatches = selectedPreset && Array.isArray(selectedPreset.swatches)
        ? selectedPreset.swatches
        : []
      const rowAccents = selectedPreset && Array.isArray(selectedPreset.rowAccents) && selectedPreset.rowAccents.length >= 6
        ? selectedPreset.rowAccents
        : ['#60a5fa', '#34d399', '#f59e0b', '#f472b6', '#a78bfa', '#22d3ee']
      const accentStrong = presetSwatches[1] || settings.accentColor || '#60a5fa'
      const accentBase = presetSwatches[2] || settings.accentColor || '#60a5fa'
      const accentSoft = presetSwatches[3] || '#93c5fd'
      const dangerBase = selectedPreset?.dangerColor || '#ef4444'
      const warningBase = selectedPreset?.warningColor || '#f59e0b'
      const shadowMap = {
        light: '0 10px 18px rgba(15, 23, 42, 0.18)',
        medium: '0 10px 22px rgba(15, 23, 42, 0.22)',
        strong: '0 18px 40px rgba(15, 23, 42, 0.32)',
      }
      const cardBg = settings.cardBackground || '#1c233a'
      const autoText = this.getReadableTextColor(cardBg)
      const baseText = settings.fontColor && settings.fontColor !== 'auto'
        ? settings.fontColor
        : autoText
      const cardText = settings.cardTextColor && settings.cardTextColor !== 'auto'
        ? settings.cardTextColor
        : baseText
      return {
        '--prep-card-bg': cardBg,
        '--prep-card-border': settings.cardBorder,
        '--prep-text': cardText,
        '--prep-muted': baseText,
        '--prep-primary': settings.accentColor,
        '--prep-font-family': settings.fontFamily,
        '--prep-card-radius': `${settings.cardRadius || 14}px`,
        '--prep-card-shadow': shadowMap[settings.cardShadow] || shadowMap.medium,
        '--prep-settings-panel-bg': cardBg,
        '--prep-settings-bg': cardBg,
        '--prep-settings-text': baseText,
        '--prep-settings-item-bg': cardBg,
        '--prep-settings-subtext': baseText,
        '--prep-settings-subdot': baseText,
        '--prep-settings-border': settings.cardBorder || 'rgba(148, 163, 184, 0.3)',
        '--prep-role-bg': settings.accentColor,
        '--prep-role-text': baseText,
        '--prep-accent-strong': accentStrong,
        '--prep-accent-base': accentBase,
        '--prep-accent-soft': accentSoft,
        '--prep-warning-base': warningBase,
        '--prep-danger-base': dangerBase,
        '--prep-surface-1': `color-mix(in srgb, ${cardBg} 78%, #0b1220 22%)`,
        '--prep-surface-2': `color-mix(in srgb, ${cardBg} 58%, #0b1220 42%)`,
        '--prep-focus-ring': `color-mix(in srgb, ${accentBase} 48%, transparent 52%)`,
        '--prep-field-stroke': `color-mix(in srgb, ${accentSoft} 24%, rgba(148, 163, 184, 0.35) 76%)`,
        '--prep-glow-soft': `color-mix(in srgb, ${accentBase} 22%, transparent 78%)`,
        '--prep-row-accent-1': rowAccents[0],
        '--prep-row-accent-2': rowAccents[1],
        '--prep-row-accent-3': rowAccents[2],
        '--prep-row-accent-4': rowAccents[3],
        '--prep-row-accent-5': rowAccents[4],
        '--prep-row-accent-6': rowAccents[5],
      }
    },
    canvasStyle() {
      const settings = this.activeSettings
      if (this.previewBackgroundUrl) {
        return {
          backgroundImage: `url("${this.previewBackgroundUrl}")`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat',
        }
      }
      if (settings.backgroundType === 'image' && settings.backgroundImage) {
        const url = this.resolveBackendAsset(settings.backgroundImage)
        return {
          backgroundImage: `url("${url}")`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat',
        }
      }
      return {
        backgroundColor: settings.backgroundColor,
      }
    },
    selectedAssignedOffers() {
      if (!this.selectedAssigneeId) return []
      if (String(this.selectedAssigneeId) === 'not-project') {
        return this.notProjectOffers
      }
      if (String(this.selectedAssigneeId) === 'completed-projects') {
        return this.completedProjectOffers
      }
      return this.assignedOffersByUser[this.selectedAssigneeId] || []
    },
    normalizedLeaderCardSearch() {
      return this.normalizeSearchTerm(this.leaderCardSearch)
    },
    filteredBudgetCards() {
      const query = this.normalizedLeaderCardSearch
      if (!query) return this.budgetCards
      return this.allLeaderOfferCards.filter(card => this.offerMatchesSearch(card, query))
    },
    filteredSelectedAssignedOffers() {
      const query = this.normalizedLeaderCardSearch
      if (!query) return this.selectedAssignedOffers
      return []
    },
    allLeaderOfferCards() {
      const seen = new Set()
      const pushUnique = (acc, card, keyPrefix = 'offer') => {
        if (!card) return
        const uniqueKey = `${keyPrefix}-${String(card.offerType || '')}-${String(card.offerId || '')}`
        if (seen.has(uniqueKey)) return
        seen.add(uniqueKey)
        acc.push(card)
      }

      const cards = []
      this.budgetCards.forEach(card => pushUnique(cards, card, 'budget'))
      Object.values(this.assignedOffersByUser || {}).forEach(list => {
        (list || []).forEach(card => pushUnique(cards, card, 'assigned'))
      })
      this.notProjectOffers.forEach(card => pushUnique(cards, card, 'not-project'))
      this.completedProjectOffers.forEach(card => pushUnique(cards, card, 'completed'))
      return cards.filter(card => this.isRenderableOffer(card))
    },
    visibleAssignedOffers() {
      if (!this.canManageAssignments) return []
      if (!this.selectedAssigneeId && !this.normalizedLeaderCardSearch) return []
      return this.filteredSelectedAssignedOffers.filter(card => this.isRenderableOffer(card))
    },
    selectedBudgetMenuKey() {
      return (this.$store && this.$store.state && this.$store.state.app && this.$store.state.app.prepBudgetActiveMenuKey) || ''
    },
    addBudgetModalMode() {
      return (this.$store && this.$store.state && this.$store.state.app && this.$store.state.app.prepBudgetAddModalMode) || 'main'
    },
    addBudgetMenuOptionsMain() {
      return (this.$store && this.$store.state && this.$store.state.app && this.$store.state.app.prepBudgetMainAddOptions) || [
        { value: 'zsaluzas-allvanyozas', text: 'Zsaluzás és állványozás' },
        { value: 'sikalapozas', text: 'Sikalapozás' },
        { value: 'melyalapozas', text: 'Mély alapozás' },
        { value: 'helyszini-beton-vasbeton', text: 'Helyszini beton és vasbeton' },
        { value: 'egyeb', text: 'Egyébb' },
        { value: 'uj', text: 'Új' },
      ]
    },
    activeAddBudgetMenuOptions() {
      if (this.addBudgetModalMode === 'sub') {
        const defaults = Array.isArray(this.addBudgetMenuOptionsSub) ? this.addBudgetMenuOptionsSub : []
        const dynamic = (this.customBudgetMenus || [])
          .filter(item => item && item.subgroupKey && item.title)
          .map(item => ({
            value: String(item.subgroupKey),
            text: String(item.title),
          }))
        const merged = [...defaults, ...dynamic]
        const unique = []
        const seen = new Set()
        merged.forEach(opt => {
          const value = String(opt && opt.value ? opt.value : '').trim()
          const text = String(opt && opt.text ? opt.text : '').trim()
          if (!value || !text || seen.has(value)) return
          seen.add(value)
          unique.push({ value, text })
        })
        return unique
      }
      return this.addBudgetMenuOptionsMain.filter(opt => String(opt.value) !== 'uj')
    },
    canDeleteAddBudgetSelection() {
      const selected = String(this.addBudgetMenuDraft || '').trim()
      if (!selected || this.addBudgetCreateMode) return false

      if (this.addBudgetModalMode === 'sub') {
        return !!this.findDeletableSubMenuByDraft(selected)
      }

      const protectedMain = ['foosszesito', 'osszesito', 'jarulekos-koltsegek']
      if (protectedMain.includes(selected)) return false
      return this.activeAddBudgetMenuOptions.some(opt => String(opt.value) === selected)
    },
    addBudgetModalOpen: {
      get() {
        return !!(this.$store && this.$store.state && this.$store.state.app && this.$store.state.app.prepBudgetAddModalOpen)
      },
      set(value) {
        if (!this.$store) return
        if (value) this.$store.commit('app/OPEN_PREP_BUDGET_ADD_MODAL')
        else this.$store.commit('app/CLOSE_PREP_BUDGET_ADD_MODAL')
      },
    },
    customBudgetMenus() {
      return (this.$store && this.$store.state && this.$store.state.app && this.$store.state.app.prepBudgetCustomMenus) || []
    },
    selectedCustomBudgetMenu() {
      const key = this.selectedBudgetMenuKey
      return this.customBudgetMenus.find(item => item.key === key) || null
    },
    contentBudgetMenu() {
      const selected = this.selectedCustomBudgetMenu
      if (!selected) return null

      const hasChildren = this.customBudgetMenus.some(
        item => item && item.parentKey === selected.key && item.subgroupKey,
      )

      // Szülő menün kattintva az első almenü tételeit mutatjuk.
      if (!selected.subgroupKey && hasChildren) {
        const firstChild = this.customBudgetMenus.find(
          item => item && item.parentKey === selected.key && item.subgroupKey,
        )
        if (firstChild) return firstChild
      }

      return selected
    },
    selectedCustomEditMode() {
      if (!this.contentBudgetMenu) return 'full'
      return this.contentBudgetMenu.subgroupKey ? 'qty-only' : 'readonly'
    },
    selectedCustomShowPositiveQtyOnly() {
      if (!this.contentBudgetMenu) return false
      return !this.contentBudgetMenu.subgroupKey
    },
    activeSubgroupOptions() {
      const sourceMenu = this.resolveMenuSource(this.contentBudgetMenu)
      if (!sourceMenu || !sourceMenu.key) return []
      return this.customBudgetMenus
        .filter(item => item.parentKey === sourceMenu.key && item.subgroupKey)
        .map(item => ({
          value: item.subgroupKey,
          text: item.title,
        }))
    },
    isDeepFoundationSelected() {
      return this.selectedBudgetMenuKey === 'melyalapozas'
    },
    isStripFoundationSelected() {
      return this.selectedBudgetMenuKey === 'sikalapozas'
    },
    isAlapozasSelected() {
      return this.selectedBudgetMenuKey === 'alapozas'
    },
    isWaterproofingSelected() {
      return this.selectedBudgetMenuKey === 'viztelenites'
    },
    isEarthworkSelected() {
      return this.selectedBudgetMenuKey === 'foldmunka'
    },
    isEarthwork2Selected() {
      return this.selectedBudgetMenuKey === 'foldmunka-2'
    },
    isFormworkSelected() {
      return this.selectedBudgetMenuKey === 'zsaluzas-allvanyozas'
    },
    isConcreteSelected() {
      return this.selectedBudgetMenuKey === 'helyszini-beton-vasbeton'
    },
    isOtherSelected() {
      return this.selectedBudgetMenuKey === 'egyeb'
    },
    isOptionalWorksSelected() {
      return this.selectedBudgetMenuKey === 'opcios-munkak'
    },
    isJarulekosSelected() {
      return this.selectedBudgetMenuKey === 'jarulekos-koltsegek'
    },
    isMainSummarySelected() {
      return this.selectedBudgetMenuKey === 'foosszesito'
    },
    isOsszesitoSelected() {
      return this.selectedBudgetMenuKey === 'osszesito'
    },
    correctionConsoleStyle() {
      return {
        transform: `translate(${this.correctionConsoleX}px, ${this.correctionConsoleY}px)`,
      }
    },
    activeBudgetOfferId() {
      const id = Number(this.detailOffer && this.detailOffer.offerId)
      return Number.isFinite(id) && id > 0 ? id : null
    },
    activeBudgetOfferType() {
      const type = this.detailOffer && this.detailOffer.offerType
      return type === 'project' || type === 'task' ? type : ''
    },
  },
  watch: {
    backgroundImageFile(newFile) {
      if (!newFile) {
        this.previewBackgroundUrl = ''
        return
      }
      try {
        this.previewBackgroundUrl = URL.createObjectURL(newFile)
      } catch (e) {
        this.previewBackgroundUrl = ''
      }
    },
    selectedAssigneeId(newId) {
      if (!newId) return
      this.$nextTick(() => {
        this.ensureAssignedPositions()
        this.setupDrag()
        this.setupDropzones()
      })
    },
    async selectedBudgetMenuKey(newKey, oldKey) {
      if (newKey !== 'korrekcio') return
      this.correctionDraftGlobalPercent = Number(this.correctionSettings.globalPercent || 0)
      this.correctionDraftMaterialPercent = Number(this.correctionSettings.materialPercent || 0)
      this.correctionDraftFeePercent = Number(this.correctionSettings.feePercent || 0)
      await this.syncCorrectionBaseNetFromSummary()
      this.correctionModalOpen = true
      if (!this.$store) return
      const fallbackKey = oldKey && oldKey !== 'korrekcio' ? oldKey : 'jarulekos-koltsegek'
      this.$store.commit('app/SET_PREP_BUDGET_ACTIVE_MENU', fallbackKey)
      this.$nextTick(() => {
        const table = this.getImportTargetRef()
        if (table && typeof table.applyCorrectionPreview === 'function') {
          table.applyCorrectionPreview(this.getCorrectionPreviewPayload())
        }
      })
    },
    '$store.state.app.prepBudgetMode': function (val) {
      if (!val && this.detailOffer) {
        this.detailOffer = null
      }
    },
    $route: {
      handler() {
        this.syncBudgetRoute()
      },
      immediate: true,
    },
    customBudgetMenus: {
      handler() {
        this.ensureFixedSikalapozasMenus()
        this.scheduleMenuConfigPersist()
      },
      deep: true,
    },
    addBudgetModalOpen(newVal) {
      if (newVal && this.addBudgetModalMode === 'main') {
        this.loadMainAddOptionsFromApi()
      }
    },
  },
  created() {
    if (typeof document !== 'undefined') {
      document.body.classList.add('prep-theme-preparation')
    }
    const currentUser = JSON.parse(localStorage.getItem('user') || '{}')
    this.currentUserId = currentUser.id || 'guest'
    this.positions = this.loadPositions()
    this.loadPrepUi()
    this.loadPreparatoryUsers()
    this.ensureFixedSikalapozasMenus()
    this.loadMainAddOptionsFromApi()
    if (typeof window !== 'undefined') {
      window.addEventListener('prep-open-project-price-import', this.handleProjectPriceImportTrigger)
      window.addEventListener('resize', this.onBoardResize)
    }
  },
  beforeDestroy() {
    if (typeof document !== 'undefined') {
      document.body.classList.remove('prep-theme-preparation')
    }
    this.stopLeaderAutoRefresh()
    this.stopCorrectionDrag()
    if (typeof window !== 'undefined') {
      window.removeEventListener('prep-open-project-price-import', this.handleProjectPriceImportTrigger)
      window.removeEventListener('resize', this.onBoardResize)
    }
    if (this.menuConfigPersistTimer) {
      clearTimeout(this.menuConfigPersistTimer)
      this.menuConfigPersistTimer = null
    }
  },
  methods: {
    onBoardResize() {
      this.$nextTick(() => {
        this.ensureDefaultPositions()
        if (this.selectedAssigneeId) this.ensureAssignedPositions()
      })
    },
    getImportTargetRef() {
      const table = this.$refs.importTarget
      if (Array.isArray(table)) return table[0] || null
      return table || null
    },
    getCurrentNetTotalFromTable() {
      const table = this.getImportTargetRef()
      const raw = table && Number.isFinite(Number(table.totalNet))
        ? Number(table.totalNet)
        : 0
      return Math.round(raw)
    },
    async fetchSummaryTotals() {
      const offerType = String(this.activeBudgetOfferType || '')
      const offerId = Number(this.activeBudgetOfferId || 0)
      if (!offerType || !offerId) {
        return { material: 0, fee: 0, net: 0 }
      }

      const response = await axios({
        method: 'get',
        url: 'budget/offer/entries',
        params: {
          offer_type: offerType,
          offer_id: offerId,
          category: 'osszesito',
          subgroup_key: '',
        },
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
      })

      const payload = response.data || {}
      const items = Array.isArray(payload)
        ? payload
        : (Array.isArray(payload.rows) ? payload.rows : [])

      const total = items.reduce(
        (sum, row) => sum + (Number(row.material_total) || 0) + (Number(row.fee_total) || 0),
        0,
      )
      const material = items.reduce((sum, row) => sum + (Number(row.material_total) || 0), 0)
      const fee = items.reduce((sum, row) => sum + (Number(row.fee_total) || 0), 0)
      return {
        material: Number(material.toFixed(2)),
        fee: Number(fee.toFixed(2)),
        net: Math.round(total),
      }
    },
    async syncCorrectionBaseNetFromSummary() {
      try {
        const totals = await this.fetchSummaryTotals()
        this.correctionSummaryBaseMaterial = Number(totals.material || 0)
        this.correctionSummaryBaseFee = Number(totals.fee || 0)
        this.correctionNegotiatedBaseNet = Number(totals.net || 0)
        this.correctionDraftNegotiatedNet = this.correctionNegotiatedBaseNet
      } catch (e) {
        // Do not fallback to current table totals; keep the last DB-based value.
        this.notify('Összesítő nettó beolvasása sikertelen', 'danger', 'XOctagonIcon')
      }
    },
    normalizeNegotiatedNet() {
      this.correctionDraftNegotiatedNet = Math.round(Number(this.correctionDraftNegotiatedNet || 0))
      this.applyCorrectionPreviewChanges()
    },
    formatNegotiatedNetInput(value) {
      const num = Math.round(Number(value || 0))
      if (!Number.isFinite(num)) return '0'
      return num.toLocaleString('hu-HU')
    },
    onNegotiatedNetInput(rawValue) {
      const cleaned = String(rawValue || '').replace(/[^\d-]/g, '')
      const parsed = Number(cleaned || 0)
      this.correctionDraftNegotiatedNet = Number.isFinite(parsed) ? parsed : 0
      this.applyCorrectionPreviewChanges()
    },
    startCorrectionDrag(event) {
      this.correctionDragging = true
      this.correctionDragOffsetX = event.clientX - this.correctionConsoleX
      this.correctionDragOffsetY = event.clientY - this.correctionConsoleY
      window.addEventListener('mousemove', this.onCorrectionDrag)
      window.addEventListener('mouseup', this.stopCorrectionDrag)
    },
    onCorrectionDrag(event) {
      if (!this.correctionDragging) return
      this.correctionConsoleX = Math.max(0, event.clientX - this.correctionDragOffsetX)
      this.correctionConsoleY = Math.max(0, event.clientY - this.correctionDragOffsetY)
    },
    stopCorrectionDrag() {
      this.correctionDragging = false
      window.removeEventListener('mousemove', this.onCorrectionDrag)
      window.removeEventListener('mouseup', this.stopCorrectionDrag)
    },
    notify(title, variant = 'success', icon = 'CheckIcon') {
      this.$toast({
        component: ToastificationContent,
        props: {
          title,
          icon,
          variant,
        },
      })
    },
    normalizeCorrectionPercent(value) {
      return Math.max(-100, Math.min(1000, Number(value || 0)))
    },
    getNegotiatedDerivedPercent() {
      const targetNet = Math.round(Number(this.correctionDraftNegotiatedNet || 0))
      const baseNet = Math.round(Number(this.correctionNegotiatedBaseNet || 0))
      const delta = Number((targetNet - baseNet).toFixed(2))
      const affectMaterial = !!this.correctionNegotiatedAffectsMaterial
      const affectFee = !!this.correctionNegotiatedAffectsFee
      if ((!affectMaterial && !affectFee) || Math.abs(delta) < 0.01) return 0

      let denominator = 0
      if (affectMaterial && affectFee) {
        denominator = Number(this.correctionSummaryBaseMaterial || 0) + Number(this.correctionSummaryBaseFee || 0)
      } else if (affectMaterial) {
        denominator = Number(this.correctionSummaryBaseMaterial || 0)
      } else {
        denominator = Number(this.correctionSummaryBaseFee || 0)
      }
      if (!Number.isFinite(denominator) || Math.abs(denominator) < 0.01) return 0
      return (delta / denominator) * 100
    },
    getCorrectionPreviewPayload() {
      const negotiatedPercent = this.getNegotiatedDerivedPercent()
      const affectMaterial = !!this.correctionNegotiatedAffectsMaterial
      const affectFee = !!this.correctionNegotiatedAffectsFee
      return {
        globalPercent: Number(this.correctionDraftGlobalPercent || 0),
        materialPercent: Number(this.correctionDraftMaterialPercent || 0) + (affectMaterial ? negotiatedPercent : 0),
        feePercent: Number(this.correctionDraftFeePercent || 0) + (affectFee ? negotiatedPercent : 0),
        negotiatedEnabled: false,
        negotiatedNet: Math.round(Number(this.correctionDraftNegotiatedNet || 0)),
        negotiatedMaterial: affectMaterial,
        negotiatedFee: affectFee,
      }
    },
    applyCorrectionPreviewChanges() {
      const table = this.getImportTargetRef()
      if (table && typeof table.applyCorrectionPreview === 'function') {
        table.applyCorrectionPreview(this.getCorrectionPreviewPayload())
      }
    },
    adjustCorrectionPercent(kind, delta) {
      const step = Number(delta || 0)
      if (!step) return
      if (kind === 'material') {
        const current = Number(this.correctionDraftMaterialPercent || 0)
        this.correctionDraftMaterialPercent = this.normalizeCorrectionPercent(current + step)
      } else if (kind === 'fee') {
        const current = Number(this.correctionDraftFeePercent || 0)
        this.correctionDraftFeePercent = this.normalizeCorrectionPercent(current + step)
      } else {
        const current = Number(this.correctionDraftGlobalPercent || 0)
        this.correctionDraftGlobalPercent = this.normalizeCorrectionPercent(current + step)
      }
      this.applyCorrectionPreviewChanges()
    },
    cancelCorrectionChanges() {
      this.correctionDraftGlobalPercent = Number(this.correctionSettings.globalPercent || 0)
      this.correctionDraftMaterialPercent = Number(this.correctionSettings.materialPercent || 0)
      this.correctionDraftFeePercent = Number(this.correctionSettings.feePercent || 0)
      this.correctionDraftNegotiatedNet = this.correctionNegotiatedBaseNet
      const table = this.getImportTargetRef()
      if (table && typeof table.resetCorrectionPreview === 'function') {
        table.resetCorrectionPreview()
      }
      this.correctionModalOpen = false
    },
    saveCorrectionChanges() {
      const offerType = String(this.activeBudgetOfferType || '')
      const offerId = Number(this.activeBudgetOfferId || 0)
      const globalPercent = this.normalizeCorrectionPercent(this.correctionDraftGlobalPercent)
      const negotiatedPercent = this.getNegotiatedDerivedPercent()
      const negotiatedNet = Math.round(Number(this.correctionDraftNegotiatedNet || 0))
      const negotiatedMaterial = !!this.correctionNegotiatedAffectsMaterial
      const negotiatedFee = !!this.correctionNegotiatedAffectsFee
      const materialPercent = this.normalizeCorrectionPercent(
        Number(this.correctionDraftMaterialPercent || 0) + (negotiatedMaterial ? negotiatedPercent : 0),
      )
      const feePercent = this.normalizeCorrectionPercent(
        Number(this.correctionDraftFeePercent || 0) + (negotiatedFee ? negotiatedPercent : 0),
      )
      if (!offerType || !offerId) return

      this.correctionSaving = true
      axios({
        method: 'post',
        url: 'budget/offer/apply-correction',
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
        data: {
          offer_type: offerType,
          offer_id: offerId,
          global_percent: globalPercent,
          material_percent: materialPercent,
          fee_percent: feePercent,
          negotiated_enabled: 0,
          negotiated_net: negotiatedNet,
          negotiated_material: negotiatedMaterial ? 1 : 0,
          negotiated_fee: negotiatedFee ? 1 : 0,
        },
      }).then(() => {
        this.correctionSettings.globalPercent = globalPercent
        this.correctionSettings.materialPercent = materialPercent
        this.correctionSettings.feePercent = feePercent
        this.correctionModalOpen = false
        const table = this.getImportTargetRef()
        if (table && typeof table.fetchItems === 'function') {
          table.fetchItems(false)
        }
        this.notify('Korrekció mentve', 'success', 'CheckIcon')
      }).catch(() => {
        this.notify('Korrekció mentése sikertelen', 'danger', 'XOctagonIcon')
      }).finally(() => {
        this.correctionSaving = false
      })
    },
    handleProjectPriceImportTrigger() {
      if (!this.$store) return
      const currentKey = this.selectedBudgetMenuKey
      const allowed = [
        'jarulekos-koltsegek',
        'zsaluzas-allvanyozas',
        'sikalapozas',
        'alapozas',
        'viztelenites',
        'foldmunka',
        'foldmunka-2',
        'melyalapozas',
        'helyszini-beton-vasbeton',
        'egyeb',
      ]
      if (!allowed.includes(currentKey)) {
        this.$store.commit('app/SET_PREP_BUDGET_ACTIVE_MENU', 'jarulekos-koltsegek')
      }
      this.$nextTick(() => {
        const table = this.getImportTargetRef()
        if (table && typeof table.openProjectImportModal === 'function') {
          table.openProjectImportModal()
        }
      })
    },
    getMenuConfigKey(offerType, offerId) {
      const type = String(offerType || '')
      const id = Number(offerId || 0)
      if (!type || !id) return ''
      return `${type}:${id}`
    },
    async loadMenuConfigForOffer(offerType, offerId) {
      const key = this.getMenuConfigKey(offerType, offerId)
      if (!key || !this.$store) return
      try {
        const token = localStorage.getItem('token')
        const response = await axios({
          method: 'get',
          url: 'budget/offer/menu-config',
          params: {
            offer_type: String(offerType),
            offer_id: Number(offerId),
          },
          headers: {
            Authorization: `Bearer ${token}`,
          },
        })
        const menus = (response.data && Array.isArray(response.data.custom_menus)) ? response.data.custom_menus : []
        this.suppressMenuConfigPersist = true
        this.$store.commit('app/SET_PREP_BUDGET_CUSTOM_MENUS', menus)
      } catch (e) {
        this.suppressMenuConfigPersist = true
        this.$store.commit('app/SET_PREP_BUDGET_CUSTOM_MENUS', [])
      } finally {
        this.menuConfigLoadedKey = key
        this.$nextTick(() => {
          this.suppressMenuConfigPersist = false
        })
      }
    },
    scheduleMenuConfigPersist() {
      if (this.suppressMenuConfigPersist) return
      const offerType = this.activeBudgetOfferType
      const offerId = this.activeBudgetOfferId
      const key = this.getMenuConfigKey(offerType, offerId)
      if (!key || !this.detailOffer || this.menuConfigLoadedKey !== key) return
      if (this.menuConfigPersistTimer) clearTimeout(this.menuConfigPersistTimer)
      this.menuConfigPersistTimer = setTimeout(() => {
        this.menuConfigPersistTimer = null
        this.persistMenuConfigNow()
      }, 250)
    },
    async persistMenuConfigNow() {
      if (this.suppressMenuConfigPersist) return
      const offerType = this.activeBudgetOfferType
      const offerId = this.activeBudgetOfferId
      const key = this.getMenuConfigKey(offerType, offerId)
      if (!key || !this.detailOffer) return
      try {
        const token = localStorage.getItem('token')
        await axios({
          method: 'post',
          url: 'budget/offer/menu-config',
          headers: {
            Authorization: `Bearer ${token}`,
          },
          data: {
            offer_type: String(offerType),
            offer_id: Number(offerId),
            custom_menus: this.customBudgetMenus,
          },
        })
        this.menuConfigLoadedKey = key
      } catch (e) {
        // csendes hiba: UI marad használható
      }
    },
    async loadMainAddOptionsFromApi() {
      if (!this.$store) return
      try {
        const token = localStorage.getItem('token')
        const response = await axios({
          method: 'get',
          url: 'budget/categories',
          headers: {
            Authorization: `Bearer ${token}`,
          },
        })
        const rows = (response.data && Array.isArray(response.data.rows)) ? response.data.rows : []
        const options = rows
          .map(row => ({
            value: String(row.category || ''),
            text: this.getCategoryDisplayTitle(String(row.category || ''), String(row.title || row.category || '')),
          }))
          .filter(opt => opt.value && opt.text)
          .filter(opt => String(opt.value).toLowerCase() !== 'alapozas')
        this.$store.commit('app/SET_PREP_BUDGET_MAIN_ADD_OPTIONS', options)
      } catch (e) {
        // fallback: marad az aktuális (store) lista
      }
    },
    getCategoryDisplayTitle(category, fallbackTitle = '') {
      const key = String(category || '').trim().toLowerCase()
      const huMap = {
        'foosszesito': 'Főösszesítő',
        'osszesito': 'Összesítő',
        'jarulekos-koltsegek': 'Járulékos költségek',
        'zsaluzas-allvanyozas': 'Zsaluzás és állványozás',
        'sikalapozas': 'Sikalapozás',
        'melyalapozas': 'Mélyalapozás',
        'helyszini-beton-vasbeton': 'Helyszíni beton vasbeton',
        'egyeb': 'Egyéb',
        'viztelenites': 'Víztelenítés',
        'foldmunka': 'Földmunka',
        'foldmunka-2': 'Földmunka 2',
        'opcios-munkak': 'Opciós munkák',
      }
      if (huMap[key]) return huMap[key]
      const text = String(fallbackTitle || category || '').trim()
      return text
    },
    findCustomMenuByKey(key) {
      if (!key) return null
      return this.customBudgetMenus.find(item => item.key === key) || null
    },
    resolveMenuSource(menu) {
      let current = menu || null
      while (current) {
        if (current.endpoint && current.categoryKey) return current
        current = this.findCustomMenuByKey(current.parentKey)
      }
      return null
    },
    resolveRootCustomParent(menu) {
      let current = menu || null
      let guard = 0
      while (current && current.parentKey && guard < 20) {
        const next = this.findCustomMenuByKey(current.parentKey)
        if (!next) break
        current = next
        guard += 1
      }
      return current
    },
    async ensureSikalapozasFixedSubmenu(parentMenuKey) {
      if (!this.$store || !parentMenuKey) return null
      let child = this.customBudgetMenus.find(item => item.parentKey === parentMenuKey && item.subgroupKey === 'alapozas') || null
      if (!child) {
        child = {
          key: `${parentMenuKey}-alapozas-fixed`,
          title: 'Alapozás',
          parentKey: parentMenuKey,
          endpoint: 'budget/items/sikalapozas',
          categoryKey: 'sikalapozas',
          subgroupKey: 'alapozas',
          type: 'subgroup',
        }
        this.$store.commit('app/ADD_PREP_BUDGET_CUSTOM_MENU', child)
      }

      await this.assignAllSikalapozasItemsToAlapozas()
      return child
    },
    async assignAllSikalapozasItemsToAlapozas() {
      try {
        const token = localStorage.getItem('token')
        const itemsResponse = await axios({
          method: 'get',
          url: 'budget/subgroup-mapping/items',
          params: { category: 'sikalapozas' },
          headers: {
            Authorization: `Bearer ${token}`,
          },
        })
        const rows = (itemsResponse.data && Array.isArray(itemsResponse.data.rows)) ? itemsResponse.data.rows : []
        const ids = rows.map(row => Number(row.id)).filter(id => Number.isFinite(id) && id > 0)
        if (ids.length) {
          await axios({
            method: 'post',
            url: 'budget/subgroup-mapping/assign',
            headers: {
              Authorization: `Bearer ${token}`,
            },
            data: {
              category: 'sikalapozas',
              subgroup_key: 'alapozas',
              item_ids: ids,
            },
          })
        }
      } catch (e) {
        // no-op
      }
    },
    async ensureFixedSikalapozasMenus() {
      if (!this.$store || this.syncingFixedSikalapozasMenu) return
      const roots = this.customBudgetMenus.filter(item => !item.parentKey && !item.subgroupKey && item.categoryKey === 'sikalapozas')
      if (!roots.length) return

      this.syncingFixedSikalapozasMenu = true
      try {
        for (const root of roots) {
          await this.ensureSikalapozasFixedSubmenu(root.key)
        }
      } finally {
        this.syncingFixedSikalapozasMenu = false
      }
    },
    resetAddBudgetMenuDraft() {
      const opts = this.activeAddBudgetMenuOptions
      this.addBudgetMenuDraft = (opts[0] && opts[0].value) || ''
      this.addBudgetNewMenuTitle = ''
      this.addBudgetCreateMode = false
    },
    toggleCreateMainMenuMode() {
      this.addBudgetCreateMode = !this.addBudgetCreateMode
      if (!this.addBudgetCreateMode) {
        this.addBudgetNewMenuTitle = ''
      }
    },
    findDeletableSubMenuByDraft(selectedSubgroup) {
      const subgroup = String(selectedSubgroup || '').trim()
      if (!subgroup) return null
      const currentMenu = this.selectedCustomBudgetMenu || this.findCustomMenuByKey(this.selectedBudgetMenuKey)
      const rootMenu = this.resolveRootCustomParent(currentMenu)
      const parentKey = rootMenu ? rootMenu.key : this.selectedBudgetMenuKey
      if (!parentKey) return null
      return this.customBudgetMenus.find(
        item => item && item.parentKey === parentKey && String(item.subgroupKey || '') === subgroup,
      ) || null
    },
    async deleteSelectedAddBudgetOption() {
      if (!this.canDeleteAddBudgetSelection) return
      const selected = String(this.addBudgetMenuDraft || '').trim()
      if (!selected) return

      if (this.addBudgetModalMode === 'sub') {
        const target = this.findDeletableSubMenuByDraft(selected)
        if (!target || !target.key) return
        const ok = window.confirm(`Biztos törlöd ezt az almenüt? (${target.title})`)
        if (!ok) return
        this.$store.commit('app/REMOVE_PREP_BUDGET_CUSTOM_MENU', target.key)
        if (this.selectedBudgetMenuKey === target.key) {
          this.$store.commit('app/SET_PREP_BUDGET_ACTIVE_MENU', '')
        }
        this.resetAddBudgetMenuDraft()
        this.notify('Almenü törölve', 'success', 'CheckIcon')
        return
      }

      const ok = window.confirm(`Biztos törlöd ezt a főmenü kategóriát? (${selected})`)
      if (!ok) return
      try {
        const token = localStorage.getItem('token')
        await axios({
          method: 'delete',
          url: `budget/categories/${selected}`,
          headers: {
            Authorization: `Bearer ${token}`,
          },
        })

        const toRemove = this.customBudgetMenus
          .filter(item => {
            const category = String(item && item.categoryKey ? item.categoryKey : '').trim()
            return category === selected
          })
          .map(item => String(item.key))
          .filter(Boolean)
        toRemove.forEach(menuKey => {
          this.$store.commit('app/REMOVE_PREP_BUDGET_CUSTOM_MENU', menuKey)
        })

        await this.loadMainAddOptionsFromApi()
        this.resetAddBudgetMenuDraft()
        this.notify('Főmenü kategória törölve', 'success', 'CheckIcon')
      } catch (e) {
        const msg = (e && e.response && e.response.data && e.response.data.message) ? e.response.data.message : 'Törlés sikertelen'
        this.notify(msg, 'danger', 'XOctagonIcon')
      }
    },
    createMenuKeyFromTitle(title) {
      return String(title || '')
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '')
    },
    async confirmAddBudgetMenu() {
      if (!this.$store) return
      const selected = String(this.addBudgetMenuDraft || '')

      if (this.addBudgetModalMode === 'sub') {
        const selectedOption = this.addBudgetMenuOptionsSub.find(opt => opt.value === selected)
        const currentMenu = this.selectedCustomBudgetMenu || this.findCustomMenuByKey(this.selectedBudgetMenuKey)
        const rootMenu = this.resolveRootCustomParent(currentMenu)
        const parentKey = rootMenu ? rootMenu.key : this.selectedBudgetMenuKey
        const sourceMenu = this.resolveMenuSource(rootMenu || currentMenu)
        const manualTitle = String(this.addBudgetNewMenuTitle || '').trim()
        const subgroupValue = this.addBudgetCreateMode
          ? this.createMenuKeyFromTitle(manualTitle)
          : selected
        const label = this.addBudgetCreateMode
          ? manualTitle
          : (selectedOption ? selectedOption.text : selected)
        if (!label || !subgroupValue) return
        const key = `${parentKey || 'sub'}-${subgroupValue}-${Date.now()}`
        const newMenu = {
          key,
          title: label,
          parentKey: parentKey || null,
          endpoint: sourceMenu ? sourceMenu.endpoint : '',
          categoryKey: sourceMenu ? sourceMenu.categoryKey : '',
          subgroupKey: subgroupValue || '',
          type: sourceMenu ? 'subgroup' : 'uj',
        }
        this.$store.commit('app/ADD_PREP_BUDGET_CUSTOM_MENU', newMenu)
        this.$store.commit('app/SET_PREP_BUDGET_ACTIVE_MENU', newMenu.key)
        this.$store.commit('app/CLOSE_PREP_BUDGET_ADD_MODAL')
        this.resetAddBudgetMenuDraft()
        return
      }

      const selectedMainOption = this.addBudgetMenuOptionsMain.find(opt => String(opt.value) === selected) || null
      let newMenu = null
      if (this.addBudgetCreateMode) {
        const title = String(this.addBudgetNewMenuTitle || '').trim()
        if (!title) return
        const token = localStorage.getItem('token')
        const createResponse = await axios({
          method: 'post',
          url: 'budget/categories',
          headers: {
            Authorization: `Bearer ${token}`,
          },
          data: { title },
        })
        const row = createResponse.data && createResponse.data.row ? createResponse.data.row : null
        if (!row || !row.category) return
        const base = String(row.category)
        const key = this.customBudgetMenus.some(item => String(item.key) === base) ? `${base}-${Date.now()}` : base
        newMenu = {
          key,
          title: String(row.title || title),
          parentKey: null,
          endpoint: `budget/items/category/${base}`,
          categoryKey: base,
          type: 'uj',
        }
        this.$store.commit('app/ADD_PREP_BUDGET_MAIN_ADD_OPTION', { value: base, text: String(row.title || title) })
        await this.loadMainAddOptionsFromApi()
      } else if (!newMenu && selectedMainOption) {
        const base = selectedMainOption.value
        const key = this.customBudgetMenus.some(item => String(item.key) === String(base))
          ? `${base}-${Date.now()}`
          : String(base)
        newMenu = {
          key,
          title: selectedMainOption.text,
          parentKey: null,
          endpoint: `budget/items/category/${base}`,
          categoryKey: String(base),
          type: 'uj',
        }
      }
      if (!newMenu) return

      this.$store.commit('app/ADD_PREP_BUDGET_CUSTOM_MENU', newMenu)
      if (selected === 'sikalapozas') {
        const fixedSubmenu = await this.ensureSikalapozasFixedSubmenu(newMenu.key)
        this.$store.commit('app/SET_PREP_BUDGET_ACTIVE_MENU', fixedSubmenu ? fixedSubmenu.key : newMenu.key)
      } else {
        this.$store.commit('app/SET_PREP_BUDGET_ACTIVE_MENU', newMenu.key)
      }
      this.$store.commit('app/CLOSE_PREP_BUDGET_ADD_MODAL')
      this.resetAddBudgetMenuDraft()
    },
    loadPreparatoryUsers() {
      const prepRoles = ['prep_view_all', 'prep_assign_project', 'prep_view_own']
      axios({
        method: 'get',
        url: 'user/list',
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
      }).then(response => {
        const users = Array.isArray(response.data)
          ? response.data
          : (response.data && response.data.data) || []
        this.assigneeLookupByName = Object.fromEntries(
          users
            .filter(user => user && user.name)
            .map(user => [this.normalizeName(user.name), user.id]),
        )
        const preparatoryUsers = users.filter(user => {
          const roles = Array.isArray(user.role) ? user.role : []
          if (prepRoles.some(role => roles.includes(role))) return true
          return roles.some(role => typeof role === 'string' && role.startsWith('prep_'))
        })

        const currentUser = users.find(user => String(user.id) === String(this.currentUserId))
        const currentRoles = Array.isArray(currentUser && currentUser.role) ? currentUser.role : []
        const canViewAll = currentRoles.includes('prep_view_all') || currentRoles.includes('prep_assign_project')
        const canViewOwn = currentRoles.includes('prep_view_own') || canViewAll
        const isGroupLeader = currentRoles.includes('prep_assign_project')
        const canManageAssignments = isGroupLeader || currentRoles.includes('prep_view_all')
        this.isGroupLeader = isGroupLeader
        this.canManageAssignments = canManageAssignments
        this.canReturnToLeader = false

        if (canViewOwn && !canViewAll) {
          const leaderTiles = preparatoryUsers
            .filter(user => {
              const roles = Array.isArray(user.role) ? user.role : []
              return roles.includes('prep_assign_project')
            })
            .map(user => ({
              id: user.id,
              name: user.name,
              title: 'Csoportvezető',
              avatar: this.getUserAvatar(user),
              initials: this.getInitials(user.name),
              imageError: false,
            }))
          this.users = leaderTiles
          this.canReturnToLeader = leaderTiles.length > 0
          this.loadLatestOffers({ onlyAssignedTo: this.currentUserId, limit: 50, allowedTypes: ['project', 'task'] })
          this.stopLeaderAutoRefresh()
          this.$nextTick(() => {
            this.ensureDefaultPositions()
            this.setupDrag()
            this.setupDropzones()
          })
          return
        }

        const visibleUsers = canViewAll
          ? preparatoryUsers.filter(user => {
            const roles = Array.isArray(user.role) ? user.role : []
            return !roles.includes('prep_view_all')
          })
          : (canViewOwn ? preparatoryUsers.filter(user => String(user.id) === String(this.currentUserId)) : [])

        const filteredUsers = visibleUsers

        const userTiles = filteredUsers.map(user => {
          const roles = Array.isArray(user.role) ? user.role : []
          const isBoss = roles.includes('prep_view_all')
          const isLead = roles.includes('prep_assign_project')
          return {
            id: user.id,
            name: user.name,
            title: isBoss ? 'Vezető' : (isLead ? 'Csoportvezető' : 'Előkészítő'),
            avatar: this.getUserAvatar(user),
            initials: this.getInitials(user.name),
            imageError: false,
          }
        })

        if (canManageAssignments) {
          userTiles.push({
            id: 'not-project',
            name: 'Nem lett projekt',
            title: 'Gyűjtő',
            avatar: null,
            initials: 'NP',
            imageError: false,
            readOnlyBucket: true,
          })
          userTiles.push({
            id: 'completed-projects',
            name: 'Elkészült projektek',
            title: 'Gyűjtő',
            avatar: null,
            initials: 'EP',
            imageError: false,
            readOnlyBucket: true,
          })
        }

        this.users = userTiles
        if (canManageAssignments) {
          this.budgetCards = []
          this.notProjectOffers = []
          this.completedProjectOffers = []
          this.refreshLeaderBoardCards()
          this.startLeaderAutoRefresh()
        } else {
          this.budgetCards = []
          this.notProjectOffers = []
          this.completedProjectOffers = []
          this.stopLeaderAutoRefresh()
        }
        this.$nextTick(() => {
          this.ensureDefaultPositions()
          this.setupDrag()
          this.setupDropzones()
        })
      }).catch(() => {
        this.users = []
        this.budgetCards = []
        this.canManageAssignments = false
        this.canReturnToLeader = false
        this.notProjectOffers = []
        this.completedProjectOffers = []
        this.stopLeaderAutoRefresh()
        this.assigneeLookupByName = {}
      })
    },
    startLeaderAutoRefresh() {
      this.stopLeaderAutoRefresh()
      this.leaderAutoRefreshTimer = setInterval(() => {
        this.refreshLeaderBoardCards()
      }, 20000)
    },
    stopLeaderAutoRefresh() {
      if (this.leaderAutoRefreshTimer) {
        clearInterval(this.leaderAutoRefreshTimer)
        this.leaderAutoRefreshTimer = null
      }
    },
    refreshLeaderBoardCards() {
      this.loadLatestOffers({ onlyUnassigned: true, limit: null, allowedTypes: ['project', 'task'] })
      this.loadAssignedOffers()
      this.loadNotProjectOffers()
      this.loadCompletedProjectOffers()
    },
    loadNotProjectOffers() {
      axios({
        method: 'get',
        url: 'project/offer/list',
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
      }).then(response => {
        const offers = Array.isArray(response.data)
          ? response.data
          : (response.data && response.data.data) || []
        const taskOffers = offers
          .filter(offer => String(offer.type) === 'task')
          .map(offer => ({
            key: `not-project-${offer.type}-${offer.id}`,
            offerId: offer.id,
            offerType: offer.type,
            projectName: offer.name || 'Névtelen projekt',
            customerName: offer.customer || 'Ismeretlen megrendelő',
          }))
        this.notProjectOffers = taskOffers
      }).catch(() => {
        this.notProjectOffers = []
      })
    },
    loadCompletedProjectOffers() {
      axios({
        method: 'get',
        url: 'project/archive/list',
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
      }).then(response => {
        const projects = Array.isArray(response.data)
          ? response.data
          : (response.data && response.data.data) || []
        this.completedProjectOffers = projects.map(project => ({
          key: `completed-project-${project.id}`,
          offerId: project.id,
          offerType: 'project',
          projectName: project.name || 'Névtelen projekt',
          customerName: project.customer || 'Ismeretlen megrendelő',
        }))
      }).catch(() => {
        this.completedProjectOffers = []
      })
    },
    loadLatestOffers({
      onlyAssignedTo = null,
      onlyUnassigned = false,
      limit = 10,
      allowedTypes = ['project'],
    } = {}) {
      axios({
        method: 'get',
        url: 'project/offer/list',
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
      }).then(response => {
        const offers = Array.isArray(response.data)
          ? response.data
          : (response.data && response.data.data) || []
        let filtered = offers
        if (onlyAssignedTo) {
          filtered = filtered.filter(offer => {
            const assigneeId = this.resolveOfferAssigneeId(offer)
            return String(assigneeId) === String(onlyAssignedTo)
          })
        } else if (onlyUnassigned) {
          filtered = filtered.filter(offer => {
            const assigneeId = this.resolveOfferAssigneeId(offer)
            return !assigneeId
          })
        }
        const types = Array.isArray(allowedTypes) && allowedTypes.length
          ? allowedTypes.map(type => String(type))
          : ['project']
        filtered = filtered.filter(offer => types.includes(String(offer.type)))
        const latest = Number.isFinite(Number(limit)) && Number(limit) > 0
          ? filtered.slice(0, Number(limit))
          : filtered
        this.budgetCards = latest.map(offer => ({
          key: `offer-${offer.type}-${offer.id}`,
          offerId: offer.id,
          offerType: offer.type,
          projectName: offer.name || 'Névtelen projekt',
          customerName: offer.customer || 'Ismeretlen megrendelő',
        }))
        this.$nextTick(() => {
          this.ensureDefaultPositions()
          this.setupDrag()
          this.setupDropzones()
        })
      }).catch(() => {
        this.budgetCards = []
      })
    },
    loadAssignedOffers() {
      axios({
        method: 'get',
        url: 'project/offer/list',
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
      }).then(response => {
        const offers = Array.isArray(response.data)
          ? response.data
          : (response.data && response.data.data) || []
        const assigned = {}
        offers.forEach(offer => {
          if (this.canManageAssignments) {
            if (!['project', 'task'].includes(String(offer.type))) return
          } else if (String(offer.type) !== 'project') {
            return
          }
          const prepId = this.resolveOfferAssigneeId(offer)
          if (!prepId) return
          if (!assigned[prepId]) assigned[prepId] = []
          assigned[prepId].push({
            key: `assigned-${prepId}-${offer.type}-${offer.id}`,
            offerId: offer.id,
            offerType: offer.type,
            projectName: offer.name || 'Névtelen projekt',
            customerName: offer.customer || 'Ismeretlen megrendelő',
          })
        })
        this.assignedOffersByUser = assigned
        if (this.selectedAssigneeId) {
          this.$nextTick(() => this.ensureAssignedPositions())
        }
      }).catch(() => {
        this.assignedOffersByUser = {}
      })
    },
    normalizeName(value) {
      if (!value) return ''
      return String(value)
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .trim()
        .toLowerCase()
    },
    normalizeSearchTerm(value) {
      return String(value || '')
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .replace(/[^a-zA-Z0-9\s]/g, ' ')
        .replace(/\s+/g, ' ')
        .trim()
        .toLowerCase()
    },
    offerMatchesSearch(offer, normalizedQuery) {
      if (!normalizedQuery) return true
      const name = this.normalizeSearchTerm(offer && offer.projectName)
      const customer = this.normalizeSearchTerm(offer && offer.customerName)
      const haystack = `${name} ${customer}`.trim()
      const tokens = normalizedQuery.split(' ').filter(Boolean)
      if (!tokens.length) return true
      return tokens.every(token => haystack.includes(token))
    },
    isRenderableOffer(offer) {
      if (!offer || offer.offerId === null || offer.offerId === undefined) return false
      if (!offer.offerType) return false
      if (!offer.key || !String(offer.key).trim()) return false
      return true
    },
    getOfferTitle(offer) {
      const title = String((offer && offer.projectName) || '').trim()
      return title || 'Névtelen projekt'
    },
    getOfferCustomer(offer) {
      return String((offer && offer.customerName) || '').trim()
    },
    resolveOfferAssigneeId(offer) {
      if (!offer) return null

      const directId = offer.preparatory_id ?? offer.target_id ?? null
      if (directId) return directId

      const assigneeName = offer.preparatory_name ?? offer.target_name ?? null
      if (!assigneeName) return null

      const wanted = this.normalizeName(assigneeName)
      if (Object.prototype.hasOwnProperty.call(this.assigneeLookupByName, wanted)) {
        return this.assigneeLookupByName[wanted]
      }
      const user = (this.users || []).find(candidate => this.normalizeName(candidate.name) === wanted)
      return user ? user.id : null
    },
    loadPrepUi() {
      axios({
        method: 'get',
        url: 'user/prep-ui',
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
      }).then(response => {
        const settings = response.data && response.data.settings
        if (settings) {
          this.uiSettings = this.sanitizeSettings({ ...this.uiSettings, ...settings })
        }
      })
    },
    sanitizeSettings(raw) {
      const defaults = {
        backgroundType: 'color',
        backgroundColor: '#0f172a',
        backgroundImage: '',
        stylePreset: 'aurora-neon',
        fontFamily: '"Inter", "Segoe UI", sans-serif',
        fontColor: '#e5e7eb',
        cardBackground: '#1c233a',
        cardTextColor: 'auto',
        cardBorder: 'rgba(148, 163, 184, 0.18)',
        cardRadius: 14,
        cardShadow: 'medium',
        accentColor: '#60a5fa',
      }
      const settings = { ...defaults, ...(raw || {}) }
      if (!['color', 'image'].includes(settings.backgroundType)) {
        settings.backgroundType = 'color'
      }
      if (settings.backgroundType === 'image' && !settings.backgroundImage && !this.previewBackgroundUrl) {
        settings.backgroundType = 'color'
      }
      if (!settings.backgroundColor) settings.backgroundColor = defaults.backgroundColor
      if (!settings.fontFamily) settings.fontFamily = defaults.fontFamily
      if (!settings.fontColor) settings.fontColor = defaults.fontColor
      if (!settings.cardBackground) settings.cardBackground = defaults.cardBackground
      if (!settings.cardTextColor) settings.cardTextColor = defaults.cardTextColor
      if (!settings.cardBorder) settings.cardBorder = defaults.cardBorder
      if (!settings.cardRadius) settings.cardRadius = defaults.cardRadius
      if (!settings.cardShadow) settings.cardShadow = defaults.cardShadow
      if (!settings.accentColor) settings.accentColor = defaults.accentColor
      const hasPreset = this.stylePresets.some(item => item.key === settings.stylePreset)
      if (!settings.stylePreset || !hasPreset) settings.stylePreset = defaults.stylePreset
      return settings
    },
    getReadableTextColor(hex) {
      if (!hex || !hex.startsWith('#') || hex.length < 7) return '#f8fafc'
      const r = parseInt(hex.slice(1, 3), 16)
      const g = parseInt(hex.slice(3, 5), 16)
      const b = parseInt(hex.slice(5, 7), 16)
      const yiq = (r * 299 + g * 587 + b * 114) / 1000
      return yiq >= 140 ? '#0f172a' : '#f8fafc'
    },
    resolveBackendAsset(path) {
      if (!path) return ''
      if (path.startsWith('http')) return path
      const base = (axios.defaults.baseURL || '').replace(/\/api\/?$/, '')
      return `${base}${path}`
    },
    markImageError(id) {
      const target = this.users.find(user => user.id === id)
      if (target) target.imageError = true
    },
    getUserAvatar(user) {
      if (!user) return null
      // Match the navbar behavior (production-hosted images)
      const imageName = user.image || `${user.id}.jpg`
      return `https://monolitcrm.hu/images/${imageName}`
    },
    getInitials(name) {
      if (!name) return ''
      return name
        .split(' ')
        .filter(Boolean)
        .slice(0, 2)
        .map(part => part[0].toUpperCase())
        .join('')
    },
    loadPositions() {
      const key = `prep_board_positions_${this.currentUserId}`
      try {
        return JSON.parse(localStorage.getItem(key) || '{}')
      } catch (e) {
        return {}
      }
    },
    savePositions() {
      const key = `prep_board_positions_${this.currentUserId}`
      localStorage.setItem(key, JSON.stringify(this.positions))
    },
    ensureDefaultPositions() {
      const items = [
        ...this.budgetCards.map(card => card.key),
      ]
      const layout = this.getBoardLayout()
      const startY = this.getBoardMinY()
      const gapY = 220
      items.forEach((id, index) => {
        const col = index % layout.columns
        const row = Math.floor(index / layout.columns)
        const fallbackX = layout.startX + col * layout.stepX
        const fallbackY = startY + row * gapY

        if (this.positions[id] === undefined) {
          this.positions[id] = {
            x: fallbackX,
            y: fallbackY,
          }
        } else {
          const current = this.positions[id] || { x: fallbackX, y: fallbackY }
          this.positions[id] = {
            x: Math.max(layout.startX, Math.min(layout.maxX, Number(current.x) || fallbackX)),
            y: Math.max(startY, Number(current.y) || fallbackY),
          }
        }
      })
      this.savePositions()
    },
    ensureAssignedPositions() {
      if (!this.selectedAssigneeId) return
      const assigned = this.selectedAssignedOffers
      if (!assigned.length) return
      const layout = this.getBoardLayout()
      const startY = Math.max(this.getBoardMinY(), this.canManageAssignments ? 146 : 18)
      const gapY = 220
      assigned.forEach((offer, index) => {
        const col = index % layout.columns
        const row = Math.floor(index / layout.columns)
        this.$set(this.positions, offer.key, {
          x: layout.startX + col * layout.stepX,
          y: startY + row * gapY,
        })
      })
      this.savePositions()
    },
    getBoardLayout() {
      const canvas = this.$refs && this.$refs.canvas ? this.$refs.canvas : null
      const canvasWidth = canvas && canvas.clientWidth ? canvas.clientWidth : window.innerWidth
      const cardWidth = 320
      const sidePadding = 16
      const minGap = 12
      const usableWidth = Math.max(1, canvasWidth - (sidePadding * 2))
      const columns = Math.max(1, Math.floor((usableWidth + minGap) / (cardWidth + minGap)))
      const stepX = columns > 1
        ? Math.floor((usableWidth - cardWidth) / (columns - 1))
        : 0
      const maxX = sidePadding + ((columns - 1) * stepX)
      return {
        columns,
        stepX,
        startX: sidePadding,
        maxX,
      }
    },
    getBoardMinY() {
      return 0
    },
    getPos(id) {
      return this.positions[id] || { x: 0, y: 0 }
    },
    cardStyle(id) {
      const pos = this.getPos(id)
      return {
        transform: `translate(${pos.x}px, ${pos.y}px)`,
        background: this.activeSettings.cardBackground,
        borderColor: this.activeSettings.cardBorder,
        color: this.activeSettings.cardTextColor,
      }
    },
    queuePositionUpdate(id, x, y) {
      this.dragPending = { id, x, y }
      if (this.dragFrame) return
      this.dragFrame = requestAnimationFrame(() => {
        const pending = this.dragPending
        this.dragPending = null
        this.dragFrame = null
        if (!pending) return
        this.$set(this.positions, pending.id, { x: pending.x, y: pending.y })
      })
    },
    setupDrag() {
      interact('.prep-draggable').unset()
      interact('.prep-draggable')
        .draggable({
          inertia: false,
          allowFrom: '.prep-handle',
          listeners: {
            start: event => {
              event.target.classList.add('is-dragging')
            },
            move: event => {
              const element = event.target
              const id = element.getAttribute('data-id')
              const currentX = parseFloat(element.getAttribute('data-x')) || 0
              const currentY = parseFloat(element.getAttribute('data-y')) || 0
              const layout = this.getBoardLayout()
              const minY = this.getBoardMinY()
              const x = Math.max(layout.startX, Math.min(layout.maxX, currentX + event.dx))
              const y = Math.max(minY, currentY + event.dy)
              element.setAttribute('data-x', x)
              element.setAttribute('data-y', y)
              element.style.transform = `translate(${x}px, ${y}px)`
              this.queuePositionUpdate(id, x, y)
            },
            end: event => {
              const element = event.target
              const id = element.getAttribute('data-id')
              const layout = this.getBoardLayout()
              const minY = this.getBoardMinY()
              const rawX = parseFloat(element.getAttribute('data-x')) || 0
              const rawY = parseFloat(element.getAttribute('data-y')) || 0
              const x = Math.max(layout.startX, Math.min(layout.maxX, rawX))
              const y = Math.max(minY, rawY)
              element.setAttribute('data-x', x)
              element.setAttribute('data-y', y)
              element.style.transform = `translate(${x}px, ${y}px)`
              this.$set(this.positions, id, { x, y })
              this.savePositions()

              // Fallback drop handling for assignee strip outside the canvas.
              const now = Date.now()
              if (now - Number(this.lastDropActionAt || 0) > 120) {
                const offerId = event.target.getAttribute('data-offer-id')
                const offerType = event.target.getAttribute('data-offer-type')
                const clientX = event.clientX ?? (event.client && event.client.x)
                const clientY = event.clientY ?? (event.client && event.client.y)
                if (offerId && offerType && Number.isFinite(clientX) && Number.isFinite(clientY)) {
                  const hovered = document.elementFromPoint(clientX, clientY)
                  const dropTarget = hovered && hovered.closest ? hovered.closest('.prep-user-drop') : null
                  if (dropTarget) {
                    const targetId = dropTarget.getAttribute('data-user-id')
                    const isLeader = dropTarget.getAttribute('data-leader') === '1'
                    this.handleOfferDrop({
                      targetId,
                      isLeader,
                      offerId,
                      offerType,
                    })
                  }
                }
              }
              event.target.classList.remove('is-dragging')
            },
          },
        })
    },
    handleOfferDrop({
      targetId, isLeader, offerId, offerType,
    }) {
      if (!targetId || !offerId || !offerType) return
      this.lastDropActionAt = Date.now()
      if (isLeader) {
        this.unassignOffer({ offerId, offerType })
      } else {
        this.assignOfferToUser({ offerId, offerType, targetId })
      }
    },
    setupDropzones() {
      interact('.prep-user-drop').unset()
      interact('.prep-user-drop').dropzone({
        accept: '.prep-budget-card',
        overlap: 0.5,
        ondragenter: event => {
          event.target.classList.add('is-drop-target')
        },
        ondragleave: event => {
          event.target.classList.remove('is-drop-target')
        },
        ondrop: event => {
          event.target.classList.remove('is-drop-target')
          const targetId = event.target.getAttribute('data-user-id')
          const isLeader = event.target.getAttribute('data-leader') === '1'
          const offerId = event.relatedTarget.getAttribute('data-offer-id')
          const offerType = event.relatedTarget.getAttribute('data-offer-type')
          this.handleOfferDrop({
            targetId, isLeader, offerId, offerType,
          })
        },
      })
    },
    assignOfferToUser({ offerId, offerType, targetId }) {
      axios({
        method: 'post',
        url: 'project/offer/assign',
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
        data: {
          offer_id: Number(offerId),
          offer_type: offerType,
          target_id: Number(targetId),
        },
      }).then(() => {
        const key = `offer-${offerType}-${offerId}`
        this.budgetCards = this.budgetCards.filter(card => card.key !== key)
        if (this.positions[key]) {
          this.$delete(this.positions, key)
          this.savePositions()
        }
        this.loadAssignedOffers()
        // Do not auto-refill after assignment; keep the visible list decreasing.
      })
    },
    unassignOffer({ offerId, offerType }) {
      const normalizedOfferId = Number(offerId)
      const normalizedOfferType = String(offerType)
      const assignedList = Object.values(this.assignedOffersByUser || {}).flat()
      const sourceOffer = assignedList.find(card => (
        Number(card.offerId) === normalizedOfferId
        && String(card.offerType) === normalizedOfferType
      ))

      axios({
        method: 'post',
        url: 'project/offer/unassign',
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
        data: {
          offer_id: normalizedOfferId,
          offer_type: normalizedOfferType,
        },
      }).then(() => {
        const key = `offer-${normalizedOfferType}-${normalizedOfferId}`
        const assignedKey = `assigned-${this.selectedAssigneeId}-${normalizedOfferType}-${normalizedOfferId}`

        // Remove the card from every assigned bucket immediately for responsive UI feedback.
        const nextAssigned = {}
        Object.entries(this.assignedOffersByUser || {}).forEach(([userId, cards]) => {
          nextAssigned[userId] = (cards || []).filter(card => !(
            Number(card.offerId) === normalizedOfferId
            && String(card.offerType) === normalizedOfferType
          ))
        })
        this.assignedOffersByUser = nextAssigned

        // Put back into leader/unassigned list if it is not visible there yet.
        const existsInBudget = this.budgetCards.some(card => (
          Number(card.offerId) === normalizedOfferId
          && String(card.offerType) === normalizedOfferType
        ))
        if (!existsInBudget && sourceOffer) {
          this.budgetCards.unshift({
            key,
            offerId: normalizedOfferId,
            offerType: normalizedOfferType,
            projectName: sourceOffer.projectName || 'Névtelen projekt',
            customerName: sourceOffer.customerName || 'Ismeretlen megrendelő',
          })
        }

        if (this.positions[assignedKey]) {
          this.$delete(this.positions, assignedKey)
        }
        if (this.positions[key]) {
          this.$delete(this.positions, key)
          this.savePositions()
        }
        this.$nextTick(() => {
          this.ensureDefaultPositions()
          this.setupDrag()
          this.setupDropzones()
        })
      })
    },
    onUserCardClick(payload) {
      if (!payload || !payload.id || !this.canManageAssignments) return
      const nextId = this.selectedAssigneeId === payload.id ? null : payload.id
      this.selectedAssigneeId = nextId
      if (nextId) {
        this.$nextTick(() => {
          this.ensureAssignedPositions()
        })
      }
      this.$emit('prep-card-click', payload)
    },
    openBudgetDetail(offer) {
      if (!offer) return
      const params = {
        offerType: offer.offerType,
        offerId: offer.offerId,
      }
      const query = {
        name: offer.projectName,
        from: this.$route.fullPath,
      }
      this.$store.commit('app/SET_PREP_BUDGET_BACK_ROUTE', this.$route.fullPath)
      this.$store.commit('app/SET_PREP_BUDGET_MENU_SET', 'default')
      this.$store.commit('app/SET_PREP_BUDGET_ACTIVE_MENU', '')
      this.$store.commit('app/SET_PREP_BUDGET_CONTEXT', {
        offerType: offer.offerType,
        offerId: Number(offer.offerId),
      })
      this.$store.commit('app/SET_PREP_BUDGET_CUSTOM_MENUS', [])
      this.$router.push({ name: 'monolit.preparation.budget', params, query })
    },
    closeBudgetDetail() {
      this.$router.push({ name: 'monolit.preparation.list' })
    },
    syncBudgetRoute() {
      const isBudget = this.$route && this.$route.meta && this.$route.meta.budgetMode
      if (!isBudget) {
        this.detailOffer = null
        this.$store.commit('app/SET_PREP_BUDGET_MODE', false)
        this.$store.commit('app/SET_PREP_BUDGET_TITLE', '')
        this.$store.commit('app/SET_PREP_BUDGET_MENU_SET', 'default')
        this.$store.commit('app/SET_PREP_BUDGET_CONTEXT', { offerType: '', offerId: null })
        this.$store.commit('app/SET_PREP_BUDGET_CUSTOM_MENUS', [])
        this.menuConfigLoadedKey = ''
        return
      }

      const fromQuery = (this.$route.query && this.$route.query.from) || ''
      if (fromQuery) {
        this.$store.commit('app/SET_PREP_BUDGET_BACK_ROUTE', fromQuery)
      }

      const { offerType, offerId } = this.$route.params || {}
      const nameFromQuery = (this.$route.query && this.$route.query.name) || ''
      const offer = this.findOfferByRoute(offerType, offerId, nameFromQuery)
      this.detailOffer = offer
      this.$store.commit('app/SET_PREP_BUDGET_MODE', true)
      this.$store.commit('app/SET_PREP_BUDGET_TITLE', offer.projectName)
      this.$store.commit('app/SET_PREP_BUDGET_MENU_SET', 'default')
      this.$store.commit('app/SET_PREP_BUDGET_CONTEXT', {
        offerType: String(offerType || ''),
        offerId: Number(offerId || 0) || null,
      })

      const offerKey = this.getMenuConfigKey(offerType, offerId)
      if (offerKey && offerKey !== this.menuConfigLoadedKey) {
        this.loadMenuConfigForOffer(offerType, offerId)
      }

      if (!offer.projectName && offerType && offerId) {
        this.fetchOfferById(offerType, offerId)
      }
    },
    findOfferByRoute(offerType, offerId, fallbackName = '') {
      const key = `${offerType}-${offerId}`
      const fromBudget = this.budgetCards.find(card => `${card.offerType}-${card.offerId}` === key)
      if (fromBudget) return { ...fromBudget }
      const assignedList = Object.values(this.assignedOffersByUser || {}).flat()
      const fromAssigned = assignedList.find(card => `${card.offerType}-${card.offerId}` === key)
      if (fromAssigned) return { ...fromAssigned }
      return {
        offerType,
        offerId: Number(offerId),
        projectName: fallbackName || 'Költségvetés',
        customerName: '',
      }
    },
    fetchOfferById(offerType, offerId) {
      axios({
        method: 'get',
        url: 'project/offer/list',
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
      }).then(response => {
        const offers = Array.isArray(response.data)
          ? response.data
          : (response.data && response.data.data) || []
        const match = offers.find(item => String(item.type) === String(offerType) && String(item.id) === String(offerId))
        if (!match) return
        const updated = {
          offerType: match.type,
          offerId: match.id,
          projectName: match.name || 'Költségvetés',
          customerName: match.customer || '',
        }
        this.detailOffer = updated
        this.$store.commit('app/SET_PREP_BUDGET_TITLE', updated.projectName)
      })
    },
    toggleSettings() {
      if (!this.settingsOpen) {
        this.settingsDraft = this.sanitizeSettings({ ...this.uiSettings })
        this.backgroundImageFile = null
        this.previewBackgroundUrl = ''
      }
      this.settingsOpen = !this.settingsOpen
    },
    openSettings() {
      this.settingsDraft = this.sanitizeSettings({ ...this.uiSettings })
      this.backgroundImageFile = null
      this.previewBackgroundUrl = ''
      this.settingsOpen = true
    },
    closeSettings() {
      this.settingsOpen = false
      this.previewBackgroundUrl = ''
    },
    toggleSection(section) {
      this.openSections[section] = !this.openSections[section]
    },
    openColorPicker(target) {
      this.colorPickerTarget = target
      this.colorPickerValue = this.settingsDraft[target] || '#0f172a'
      this.colorModalOpen = true
    },
    onColorInput(color) {
      const hex = color && color.hex ? color.hex : this.colorPickerValue
      this.colorPickerValue = hex
      this.$set(this.settingsDraft, this.colorPickerTarget, hex)
      if (this.colorPickerTarget === 'backgroundColor') {
        this.settingsDraft.backgroundType = 'color'
      }
      if (this.colorPickerTarget === 'cardBackground' && this.settingsDraft.cardTextColor === 'auto') {
        this.$set(this.settingsDraft, 'cardTextColor', 'auto')
      }
    },
    applyColorSelection() {
      this.colorModalOpen = false
    },
    openFontPicker() {
      this.fontModalOpen = true
    },
    openShadowPicker() {
      this.shadowModalOpen = true
    },
    openImagePicker() {
      this.settingsDraft.backgroundType = 'image'
      this.imageModalOpen = true
    },
    removeBackgroundImage() {
      this.settingsDraft.backgroundImage = ''
      this.backgroundImageFile = null
      this.previewBackgroundUrl = ''
    },
    applyStylePreset(presetKey) {
      const preset = this.stylePresets.find(item => item.key === presetKey)
      if (!preset) return

      this.settingsDraft.backgroundType = 'color'
      this.settingsDraft.backgroundImage = ''
      this.previewBackgroundUrl = ''
      this.backgroundImageFile = null
      this.settingsDraft.stylePreset = preset.key
      this.settingsDraft.backgroundColor = preset.backgroundColor
      this.settingsDraft.fontColor = preset.fontColor
      this.settingsDraft.cardBackground = preset.cardBackground
      this.settingsDraft.cardBorder = preset.cardBorder
      this.settingsDraft.accentColor = preset.accentColor
      this.settingsDraft.cardTextColor = 'auto'
    },
    saveSettings() {
      const sanitized = this.sanitizeSettings(this.settingsDraft)
      const formData = new FormData()
      formData.append('settings', JSON.stringify(sanitized))
      if (this.backgroundImageFile) {
        formData.append('background_image', this.backgroundImageFile)
      }

      axios({
        method: 'post',
        url: 'user/prep-ui',
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'multipart/form-data',
        },
        data: formData,
      }).then(response => {
        const settings = response.data && response.data.settings
        if (settings) {
          this.uiSettings = this.sanitizeSettings({ ...this.uiSettings, ...settings })
        } else {
          this.uiSettings = this.sanitizeSettings({ ...this.uiSettings, ...sanitized })
        }
        this.settingsOpen = false
      })
    },
    resetSettings() {
      this.settingsDraft = this.sanitizeSettings({})
      this.backgroundImageFile = null
      this.previewBackgroundUrl = ''
    },
  },
}
</script>

<style lang="scss">
.preparation-board {
  padding: 3.25rem 0 0.5rem;
  font-family: var(--prep-font-family);
  position: relative;
  color: var(--prep-text);
  --prep-settings-bg: rgba(15, 23, 42, 0.45);
  --prep-settings-panel-bg: rgba(15, 23, 42, 0.92);
  --prep-settings-border: rgba(148, 163, 184, 0.3);
  --prep-settings-text: #e5e7eb;
  --prep-settings-subtext: #cbd5f5;
  --prep-settings-subdot: rgba(203, 213, 245, 0.6);
  --prep-settings-item-bg: rgba(148, 163, 184, 0.12);
}

.prep-settings {
  position: absolute;
  top: 0px;
  right: 14px;
  z-index: 5;
}

.prep-canvas-assignee-box {
  display: flex;
  align-items: stretch;
  gap: 10px;
  position: relative;
  z-index: 3;
  padding: 0;
  border-radius: 0;
  overflow-x: auto;
  border: none;
  background: transparent;
}

.prep-assignee-strip {
  position: relative;
  margin-bottom: 12px;
  padding: 10px 12px;
  border-radius: 14px;
  border: 1px solid color-mix(in srgb, var(--prep-accent-soft) 24%, rgba(148, 163, 184, 0.22) 76%);
  background:
    linear-gradient(180deg, color-mix(in srgb, var(--prep-surface-1) 80%, #0a1020 20%) 0%, color-mix(in srgb, var(--prep-surface-2) 82%, #09101e 18%) 100%);
}

.prep-assignee-pill {
  min-width: 220px;
  max-width: 220px;
  text-align: left;
  padding: 12px 14px;
  border-radius: 12px;
  border: 1px solid rgba(148, 163, 184, 0.26);
  color: var(--prep-text);
  background: rgba(20, 30, 58, 0.78);
  cursor: pointer;
  transition: border-color 160ms ease, box-shadow 160ms ease, transform 120ms ease;
}

.prep-assignee-pill:hover {
  border-color: rgba(96, 165, 250, 0.72);
  box-shadow: 0 8px 16px rgba(7, 11, 24, 0.36);
}

.prep-assignee-pill.active {
  border-color: rgba(96, 165, 250, 0.95);
  box-shadow: 0 0 0 1px rgba(96, 165, 250, 0.32), 0 12px 22px rgba(7, 11, 24, 0.44);
}

.prep-assignee-pill-name {
  font-weight: 700;
  font-size: 1rem;
  line-height: 1.2;
}

.prep-assignee-pill-role {
  margin-top: 3px;
  color: var(--prep-muted);
  font-size: 0.9rem;
}

.prep-assignee-pill-readonly {
  background: rgba(26, 36, 68, 0.9);
}

.prep-card-search {
  position: absolute;
  top: 0px;
  right: 74px;
  z-index: 5;
  width: 280px;
}

.prep-card-search .form-control {
  height: 36px;
  border-radius: 10px;
  background: var(--prep-settings-bg);
  border: 1px solid var(--prep-settings-border);
  color: var(--prep-settings-text);
}

.prep-card-search .form-control::placeholder {
  color: var(--prep-settings-subtext);
}

@media (max-width: 900px) {
  .prep-card-search {
    width: 200px;
    right: 64px;
  }
}

.prep-settings-btn {
  background: var(--prep-settings-bg);
  color: var(--prep-settings-text);
  border-radius: 10px;
  box-shadow: none;
}

.prep-settings-btn:hover,
.prep-settings-btn:focus,
.prep-settings-btn:active {
  background: var(--prep-settings-bg) !important;
  color: var(--prep-settings-text) !important;
  box-shadow: none !important;
}

.prep-settings-panel {
  position: absolute;
  top: 48px;
  right: 0;
  width: 260px;
  max-height: 70vh;
  overflow: auto;
  background: var(--prep-settings-panel-bg);
  border: 1px solid var(--prep-settings-border);
  border-radius: 14px;
  padding: 14px;
  box-shadow: 0 18px 40px rgba(15, 23, 42, 0.4);
  color: var(--prep-settings-text);
}

.prep-settings-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 10px;
}

.prep-settings-title {
  font-weight: 600;
}

.prep-settings-close {
  width: 28px;
  height: 28px;
  padding: 0;
  border-radius: 8px;
}

.prep-settings-menu {
  display: grid;
  gap: 6px;
}

.prep-settings-menu-item {
  width: 100%;
  background: var(--prep-settings-item-bg);
  border: 1px solid var(--prep-settings-border);
  color: var(--prep-settings-text);
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-weight: 600;
  padding: 8px 10px;
  border-radius: 10px;
  cursor: pointer;
}

.prep-settings-submenu {
  display: grid;
  gap: 6px;
  padding-left: 12px;
}

.prep-style-submenu {
  max-height: 260px;
  overflow: auto;
}

.prep-settings-subitem {
  width: 100%;
  background: transparent;
  border: none;
  color: var(--prep-settings-subtext);
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 4px 4px;
  cursor: pointer;
  position: relative;
}

.prep-settings-subitem::before {
  content: '';
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: var(--prep-settings-subdot);
}

.prep-settings-subitem.active {
  color: var(--prep-settings-text);
}

.prep-settings-subitem.active::before {
  background: var(--prep-primary);
}

.prep-style-item {
  justify-content: space-between;
  gap: 10px;
}

.prep-style-item-label {
  display: inline-flex;
  align-items: center;
  gap: 8px;
}

.prep-style-dot {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  flex-shrink: 0;
}

.prep-style-swatches {
  display: inline-flex;
  align-items: center;
  gap: 3px;
}

.prep-style-swatch {
  width: 10px;
  height: 10px;
  border-radius: 2px;
  border: 1px solid rgba(255, 255, 255, 0.25);
}

.prep-color-modal {
  background: var(--prep-settings-panel-bg);
  color: var(--prep-settings-text);
}

.prep-inline-toggles {
  display: flex;
  gap: 0.5rem;
}

.prep-pill {
  flex: 1;
  padding: 6px 10px;
  border-radius: 10px;
  background: rgba(148, 163, 184, 0.12);
  color: #e5e7eb;
  border: 1px solid transparent;
  cursor: pointer;
}

.prep-pill.active {
  background: rgba(96, 165, 250, 0.2);
  border-color: rgba(96, 165, 250, 0.6);
}

.prep-color-picker {
  display: grid;
  gap: 0.5rem;
}

.prep-color-input {
  width: 100%;
  height: 42px;
  border-radius: 10px;
  border: none;
  background: transparent;
}

.prep-saturation {
  display: grid;
  gap: 0.25rem;
}

.prep-saturation-label {
  font-size: 0.85rem;
  color: #cbd5f5;
}

.prep-image-search {
  display: grid;
  gap: 0.5rem;
}

.prep-settings-actions {
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-end;
  gap: 0.5rem;
  margin-top: 1rem;
}

.prep-settings-pop-enter-active,
.prep-settings-pop-leave-active {
  transition: opacity 160ms ease, transform 160ms ease;
}

.prep-settings-pop-enter,
.prep-settings-pop-leave-to {
  opacity: 0;
  transform: translateY(-6px);
}

.prep-canvas {
  position: relative;
  height: calc(100vh - 220px);
  max-height: calc(100vh - 220px);
  min-height: 520px;
  overflow: auto;
  border-radius: 16px;
  background:
    radial-gradient(1200px 420px at 12% 0%, color-mix(in srgb, var(--prep-accent-soft) 20%, transparent 80%) 0%, transparent 65%),
    radial-gradient(1100px 420px at 88% 0%, color-mix(in srgb, var(--prep-danger-base) 9%, transparent 91%) 0%, transparent 70%),
    linear-gradient(180deg, color-mix(in srgb, var(--prep-surface-1) 84%, #0a1020 16%) 0%, color-mix(in srgb, var(--prep-surface-2) 86%, #09101e 14%) 100%);
  border: 1px solid color-mix(in srgb, var(--prep-accent-soft) 20%, rgba(148, 163, 184, 0.2) 80%);
}

.prep-canvas::before {
  display: none;
}

.prep-canvas::after {
  display: none;
}

.prep-tile {
  position: absolute;
  top: 0;
  left: 0;
  width: 320px;
  min-height: 200px;
  display: flex;
  align-items: center;
  padding: 10px 18px 10px 8px;
  background: var(--prep-card-bg);
  border: 1px solid transparent;
  border-radius: var(--prep-card-radius);
  box-shadow: var(--prep-card-shadow);
  transition: box-shadow 180ms ease, border-color 180ms ease;
  will-change: transform;
  touch-action: none;
  cursor: default;
  overflow: hidden;
  z-index: 1;
  isolation: isolate;
}

.prep-tile::before {
  content: '';
  position: absolute;
  inset: 0;
  border-radius: inherit;
  padding: 1px;
  background: linear-gradient(
    135deg,
    rgba(56, 189, 248, 0.68) 0%,
    rgba(168, 85, 247, 0.62) 30%,
    rgba(236, 72, 153, 0.64) 58%,
    rgba(251, 146, 60, 0.58) 82%,
    rgba(45, 212, 191, 0.6) 100%
  );
  -webkit-mask: linear-gradient(#000 0 0) content-box, linear-gradient(#000 0 0);
  -webkit-mask-composite: xor;
  mask-composite: exclude;
  pointer-events: none;
  opacity: 0.86;
  transition: opacity 160ms ease, filter 160ms ease;
}

.prep-tile:hover {
  box-shadow: 0 18px 40px rgba(15, 23, 42, 0.38), 0 0 0 1px rgba(96, 165, 250, 0.25);
  z-index: 3;
}

.prep-tile:hover::before {
  opacity: 1;
  filter: saturate(1.12);
}

body.dark-layout .prep-tile:hover {
  box-shadow: 0 20px 46px rgba(6, 10, 20, 0.7), 0 0 24px rgba(96, 165, 250, 0.28);
  border-color: rgba(96, 165, 250, 0.9);
}

.prep-user-drop.is-drop-target {
  box-shadow: 0 18px 40px rgba(15, 23, 42, 0.4), 0 0 0 2px rgba(96, 165, 250, 0.9);
  border-color: rgba(96, 165, 250, 0.95);
}

.prep-tile.action {
  background: linear-gradient(135deg, rgba(62, 116, 199, 0.18), rgba(62, 116, 199, 0.05));
  height: 200px;
  align-self: start;
  justify-content: center;
}

.prep-handle {
  position: absolute;
  right: 10px;
  top: 10px;
  color: var(--prep-muted);
  cursor: grab;
  font-weight: 700;
  letter-spacing: 1px;
  user-select: none;
}

.prep-action {
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  gap: 0.5rem;
}

.prep-action-icon {
  width: 52px;
  height: 52px;
  border-radius: 50%;
  background: rgba(62, 116, 199, 0.16);
  display: flex;
  align-items: center;
  justify-content: center;
  color: #3e74c7;
}

.prep-action-title {
  font-weight: 600;
  color: var(--prep-primary);
}

.prep-user {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: var(--prep-text) !important;
  width: 100%;
}

.prep-user-meta {
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
}

.prep-user-name {
  font-weight: 600;
  color: var(--prep-text) !important;
  line-height: 1.2;
}

.prep-user-role {
  align-self: flex-start;
  color: var(--prep-text);
}

.prep-budget {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  width: 100%;
  color: var(--prep-text);
  transition: transform 180ms ease;
  transform-origin: center center;
}

.prep-tile:hover .prep-budget {
  transform: translateY(-2px) scale(1.012);
}

.prep-budget-title {
  font-weight: 600;
  color: var(--prep-text);
  line-height: 1.25;
}

.prep-budget-customer {
  color: var(--prep-text);
  opacity: 0.85;
  font-size: 0.95rem;
}

.prep-photo {
  width: 98px;
  height: 130px;
  border-radius: 10px;
  overflow: hidden;
  margin-left: 0;
  background: rgba(59, 130, 246, 0.12);
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--prep-text);
  font-weight: 700;
}

.prep-photo-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: center 20%;
}

.prep-photo-fallback {
  font-size: 0.95rem;
}

.preparation-board {
  --prep-card-bg: #1c233a;
  --prep-card-border: rgba(148, 163, 184, 0.18);
  --prep-text: #e5e7eb;
  --prep-muted: #94a3b8;
  --prep-primary: #60a5fa;
}

body.dark-layout .preparation-board {
  --prep-card-bg: #1c233a;
  --prep-card-border: rgba(148, 163, 184, 0.18);
  --prep-text: #e5e7eb;
  --prep-muted: #94a3b8;
  --prep-primary: #60a5fa;
}

body:not(.dark-layout) .preparation-board {
  --prep-card-bg: #ffffff;
  --prep-card-border: rgba(59, 130, 246, 0.2);
  --prep-text: #0f172a;
  --prep-muted: #64748b;
  --prep-primary: #2563eb;
}

.prep-detail {
  position: relative;
  min-height: calc(100vh - 8rem);
  border-radius: 18px;
  padding: 2.5rem 3rem;
  background:
    linear-gradient(120deg, color-mix(in srgb, var(--prep-accent-base) 8%, transparent 92%) 0%, transparent 45%),
    transparent;
  overflow: hidden;
}

.prep-detail-body {
  position: relative;
  min-height: 60vh;
  margin-top: 2rem;
  border-radius: 18px;
  border: 1px dashed color-mix(in srgb, var(--prep-accent-soft) 24%, rgba(148, 163, 184, 0.2) 76%);
  background: linear-gradient(180deg, color-mix(in srgb, var(--prep-surface-1) 25%, transparent 75%) 0%, transparent 45%);
}

.prep-detail-placeholder {
  padding: 2rem;
  color: var(--prep-muted);
  font-size: 1rem;
}

.prep-budget-table {
  padding: 2rem;
  border-radius: 16px;
  background: linear-gradient(180deg, color-mix(in srgb, var(--prep-surface-1) 25%, transparent 75%) 0%, transparent 40%);
}

.prep-correction-row {
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: 0.9rem;
}

.prep-correction-row + .prep-correction-row {
  margin-top: 0.35rem;
}

.prep-correction-row-negotiated {
  align-items: flex-start;
}

.prep-correction-console {
  position: fixed;
  top: 0;
  left: 0;
  z-index: 1200;
  width: 350px;
  border-radius: 10px;
  border: 1px solid rgba(148, 163, 184, 0.28);
  background: rgba(30, 41, 75, 0.98);
  box-shadow: 0 16px 36px rgba(15, 23, 42, 0.45);
}

.prep-correction-console-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.65rem 0.85rem;
  border-bottom: 1px solid rgba(148, 163, 184, 0.2);
  cursor: move;
}

.prep-correction-console-title {
  font-weight: 700;
  color: #e5e7eb;
}

.prep-correction-console-close {
  width: 30px;
  height: 30px;
  border: 0;
  border-radius: 7px;
  background: rgba(15, 23, 42, 0.45);
  color: #e5e7eb;
  font-size: 1.15rem;
  line-height: 1;
  cursor: pointer;
}

.prep-correction-console-body {
  padding: 0.75rem 0.85rem 0.8rem;
}

.prep-correction-label {
  margin: 0;
  color: var(--prep-muted);
  font-size: 0.92rem;
  width: 150px;
  min-width: 150px;
}

.prep-correction-control {
  display: flex;
  align-items: center;
  gap: 0.4rem;
  margin-left: 0.6rem;
}

.prep-correction-value {
  font-weight: 700;
  color: var(--prep-text);
  width: 44px;
  min-width: 44px;
  text-align: left;
}

.prep-correction-arrows {
  display: flex;
  flex-direction: column;
  gap: 0.1rem;
}

.prep-correction-arrow {
  width: 18px;
  height: 14px;
  border: 0;
  background: transparent;
  color: var(--prep-text);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  cursor: pointer;
}

.prep-correction-negotiated-wrap {
  display: flex;
  flex-direction: column;
  gap: 0.45rem;
  margin-left: 0.6rem;
  width: 170px;
}

.prep-correction-negotiated-input {
  width: 100%;
  text-align: right;
  padding-right: 2rem;
}

.prep-correction-negotiated-input-wrap {
  position: relative;
}

.prep-correction-negotiated-currency {
  position: absolute;
  right: 0.6rem;
  top: 50%;
  transform: translateY(-50%);
  color: var(--prep-muted);
  font-size: 0.85rem;
  pointer-events: none;
}

.prep-correction-negotiated-options {
  display: flex;
  align-items: center;
  gap: 0.9rem;
}

.prep-budget-table-scroll {
  width: 100%;
  height: 100%;
  overflow: auto;
}

.prep-input {
  width: 100%;
  background:
    linear-gradient(160deg, color-mix(in srgb, var(--prep-surface-1) 88%, #ffffff 12%) 0%, var(--prep-surface-2) 100%);
  border: 1px solid var(--prep-field-stroke);
  border-radius: 8px;
  padding: 0.45rem 0.6rem;
  color: var(--prep-text);
  font-size: 0.9rem;
  transition: border-color 0.2s ease, box-shadow 0.2s ease, transform 0.15s ease;
}

.prep-input:focus {
  outline: none;
  border-color: var(--prep-accent-soft);
  box-shadow: 0 0 0 2px var(--prep-focus-ring), 0 8px 20px var(--prep-glow-soft);
  transform: translateY(-1px);
}

.prep-textarea {
  resize: vertical;
  min-height: 56px;
}

.prep-budget-item-list {
  display: flex;
  flex-direction: column;
  gap: 1.4rem;
  align-items: center;
}

.prep-budget-item-card-wrap {
  width: 100%;
  max-width: none;
  position: relative;
}

.prep-budget-item-card-wrap:nth-child(6n + 1) { --prep-row-accent: var(--prep-row-accent-1); }
.prep-budget-item-card-wrap:nth-child(6n + 2) { --prep-row-accent: var(--prep-row-accent-2); }
.prep-budget-item-card-wrap:nth-child(6n + 3) { --prep-row-accent: var(--prep-row-accent-3); }
.prep-budget-item-card-wrap:nth-child(6n + 4) { --prep-row-accent: var(--prep-row-accent-4); }
.prep-budget-item-card-wrap:nth-child(6n + 5) { --prep-row-accent: var(--prep-row-accent-5); }
.prep-budget-item-card-wrap:nth-child(6n + 6) { --prep-row-accent: var(--prep-row-accent-6); }

.prep-budget-item-card {
  display: flex;
  gap: 1.75rem;
  padding: 1rem 1.2rem;
  border-radius: 18px;
  background:
    radial-gradient(120% 120% at 0% 0%, color-mix(in srgb, var(--prep-accent-soft) 14%, transparent 86%) 0%, transparent 52%),
    linear-gradient(140deg, color-mix(in srgb, var(--prep-card-bg) 86%, #101827 14%) 0%, color-mix(in srgb, var(--prep-card-bg) 70%, #0b1324 30%) 100%);
  border: 1px solid var(--prep-card-border);
  box-shadow:
    0 12px 28px rgba(15, 23, 42, 0.2),
    inset 0 1px 0 color-mix(in srgb, #ffffff 14%, transparent 86%);
  align-items: center;
  height: 105px;
  position: relative;
  overflow: hidden;
  transition: transform 0.2s ease, box-shadow 0.2s ease, border-color 0.2s ease;
}

.prep-budget-item-card::before {
  content: '';
  position: absolute;
  inset: 0 auto 0 0;
  width: 4px;
  background: linear-gradient(180deg, color-mix(in srgb, var(--prep-row-accent, var(--prep-accent-base)) 95%, #fff 5%) 0%, transparent 100%);
  box-shadow: 0 0 20px color-mix(in srgb, var(--prep-row-accent, var(--prep-accent-base)) 50%, transparent 50%);
}

.prep-budget-item-card::after {
  content: '';
  position: absolute;
  inset: 0;
  background: linear-gradient(115deg, transparent 18%, color-mix(in srgb, #ffffff 10%, transparent 90%) 50%, transparent 82%);
  opacity: 0;
  transform: translateX(-14%);
  transition: opacity 0.22s ease, transform 0.28s ease;
  pointer-events: none;
}

.prep-budget-item-card:hover {
  border-color: color-mix(in srgb, var(--prep-accent-soft) 38%, var(--prep-card-border) 62%);
  box-shadow:
    0 18px 30px rgba(15, 23, 42, 0.28),
    0 0 0 1px color-mix(in srgb, var(--prep-accent-soft) 22%, transparent 78%);
  transform: translateY(-2px);
}

.prep-budget-item-card:hover::after {
  opacity: 1;
  transform: translateX(0);
}

.prep-budget-item-text {
  flex: 1;
  color: var(--prep-text);
  font-size: 1rem;
  line-height: 1.5;
  font-weight: 600;
  min-width: 240px;
  height: 59px;
  overflow-y: auto;
  padding: 0.35rem 0.55rem;
  border-radius: 10px;
  border: 1px solid color-mix(in srgb, var(--prep-accent-soft) 20%, rgba(148, 163, 184, 0.3) 80%);
  background: linear-gradient(160deg, color-mix(in srgb, var(--prep-surface-1) 94%, #fff 6%) 0%, var(--prep-surface-2) 100%);
  white-space: normal;
  overflow-wrap: anywhere;
  word-break: break-word;
}

.prep-budget-item-fields {
  flex: 1.2;
  display: flex;
  flex-wrap: nowrap;
  gap: 1rem;
  align-items: center;
  overflow-x: auto;
  padding-bottom: 0.25rem;
}

.prep-budget-item-field {
  display: flex;
  flex-direction: column;
  gap: 0.3rem;
  color: var(--prep-muted);
  font-size: 0.85rem;
}

.prep-budget-item-field span {
  color: var(--prep-muted);
  font-weight: 600;
  letter-spacing: 0.02em;
}

.prep-budget-item-fields .prep-budget-item-field:nth-child(3) .prep-input,
.prep-budget-item-fields .prep-budget-item-field:nth-child(5) .prep-input {
  border-color: color-mix(in srgb, #38bdf8 42%, var(--prep-field-stroke) 58%);
}

.prep-budget-item-fields .prep-budget-item-field:nth-child(4) .prep-input,
.prep-budget-item-fields .prep-budget-item-field:nth-child(6) .prep-input {
  border-color: color-mix(in srgb, #f472b6 40%, var(--prep-field-stroke) 60%);
}

.prep-budget-item-fields .prep-budget-item-field:nth-child(7) .prep-input {
  border-color: color-mix(in srgb, #f59e0b 45%, var(--prep-field-stroke) 55%);
}

.prep-budget-item-fields .prep-input {
  min-width: 90px;
}

.prep-budget-item-field .prep-input[readonly] {
  border-style: solid;
  border-color: color-mix(in srgb, var(--prep-warning-base) 30%, var(--prep-field-stroke) 70%);
  background:
    linear-gradient(145deg, color-mix(in srgb, var(--prep-surface-1) 90%, #111 10%) 0%, color-mix(in srgb, var(--prep-surface-2) 86%, #111 14%) 100%);
}

.prep-budget-save {
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 1rem;
  margin-top: 1.5rem;
}

.prep-budget-save-msg {
  color: var(--prep-muted);
  font-size: 0.9rem;
}

</style>
